import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { CollegeSearch } from "../components/CollegeSearch";
import { runComplianceChecks } from "../lib/complianceEngine";

const STEPS = [
  { key: "identity",      title: "Identity",            sub: "Capture personal details, government IDs, contact and addresses exactly as they appear on the Aadhaar / PAN cards. These become Identity Constants reused across the rest of the system." },
  { key: "address",       title: "Emergency Contact",   sub: "Next-of-kin details and any free-text notes useful for HR." },
  { key: "position",      title: "Position at SSMCH",   sub: "Date of joining, department, designation, nature of employment and appointment letter." },
  { key: "bankids",       title: "Bank, Salary & IDs",  sub: "Payroll setup and SSMCH-issued credentials." },
  { key: "qualif",        title: "Qualifications",      sub: "UG, PG and council registrations as documented on certificates." },
  { key: "experience",    title: "Past Experience",      sub: "Previous positions before joining SSMCH. Used to derive total experience, post-PG years, and time at each designation level for promotion eligibility. Skip if this is their first teaching post." },
  { key: "accommodation", title: "Accommodation",        sub: "Optional. Skip if the faculty is self-arranging accommodation." },
  { key: "compliance",    title: "NMC Compliance",      sub: "Verifying captured data against NMC Teachers Eligibility Qualifications Regulations 2022. Resolve any hard failures before save." },
  { key: "review",        title: "Review & Save",       sub: "Confirm the snapshot before adding this faculty to the directory." },
];

const DEPTS_FALLBACK = [
  "Anatomy","Physiology","Biochemistry",
  "Pathology","Microbiology","Pharmacology","Forensic Medicine and Toxicology","Community Medicine",
  "General Medicine","General Surgery","Paediatrics","Obstetrics & Gynaecology","Ophthalmology","Orthopaedics",
  "Oto-Rhino-Laryngology (ENT)","Dermatology, Venereology and Leprosy","Anaesthesiology","Psychiatry",
  "Radio-Diagnosis","Respiratory Medicine","Dentistry","Emergency Medicine","Physical Medicine and Rehabilitation"
];

const DEPT_GROUPS = [
  { label: "Pre-Clinical",  items: ["Anatomy","Biochemistry","Physiology"] },
  { label: "Para-Clinical", items: ["Community Medicine","Forensic Medicine and Toxicology","Microbiology","Pathology","Pharmacology"] },
  { label: "Clinical",      items: [
    "Anaesthesiology","Dentistry","Dermatology, Venereology and Leprosy","Emergency Medicine","General Medicine","General Surgery",
    "Obstetrics & Gynaecology","Ophthalmology","Orthopaedics","Oto-Rhino-Laryngology (ENT)",
    "Paediatrics","Physical Medicine and Rehabilitation","Psychiatry","Radio-Diagnosis","Respiratory Medicine"
  ]},
];

const DESIGS = [
  "Tutor","Demonstrator","Junior Resident","Senior Resident",
  "Assistant Professor","Associate Professor","Professor","Professor-HOD","Dean"
];

const GENDERS = ["Male", "Female", "Transgender"];
const MARITAL_STATUSES = ["Married", "Unmarried", "Divorced", "Widow"];

// Qualifications options
const UG_COURSES = [
  "MBBS","BDS",
  "BSc - Life Sciences","BSc - Zoology","BSc - Botany","BSc - Chemistry",
  "BSc - Biochemistry","BSc - Physiology","BSc - Statistics","BSc - Mathematics",
  "Others"
];
const PG_CATEGORIES = ["Medical", "Dentistry", "Others"];

const MEDICAL_COUNCILS = [
  "National Medical Commission (NMC)",
  "Medical Council of India",
  "Andhra Pradesh Medical Council","Arunachal Pradesh Medical Council","Assam Medical Council",
  "Bihar Medical Council","Chhattisgarh Medical Council","Delhi Medical Council","Goa Medical Council",
  "Gujarat Medical Council","Haryana Medical Council","Himachal Pradesh Medical Council",
  "Jammu & Kashmir Medical Council","Jharkhand Medical Council","Karnataka Medical Council",
  "Kerala Medical Council","Madhya Pradesh Medical Council","Maharashtra Medical Council",
  "Manipur Medical Council","Mizoram Medical Council","Nagaland Medical Council",
  "Odisha Medical Council","Punjab Medical Council","Rajasthan Medical Council",
  "Sikkim Medical Council","Tamil Nadu Medical Council","Telangana Medical Council",
  "Tripura Medical Council","Uttar Pradesh Medical Council","Uttarakhand Medical Council",
  "West Bengal Medical Council",
];

const DENTAL_COUNCILS = [
  "Dental Council of India",
  "Andhra Pradesh State Dental Council","Arunachal Pradesh State Dental Council","Assam State Dental Council",
  "Bihar State Dental Council","Chhattisgarh State Dental Council","Dental Council of Chandigarh",
  "Delhi State Dental Council","Goa State Dental Council","Gujarat State Dental Council",
  "Haryana State Dental Council","Himachal Pradesh State Dental Council","Jharkhand State Dental Council",
  "J & K State Dental Council","Karnataka State Dental Council","Kerala State Dental Council",
  "Madhya Pradesh State Dental Council","Maharashtra State Dental Council","Meghalaya State Dental Council",
  "Mizoram State Registration Tribunal","Manipur State Dental Council","Nagaland State Dental Council",
  "Orissa State Dental Council","State Dental Council, Puducherry","Punjab State Dental Council",
  "Rajasthan State Dental Council","Sikkim Dental Registration Tribunal","Tamil Nadu State Dental Council",
  "Tripura State Dental Council","Telangana Dental Council","Uttar Pradesh State Dental Council",
  "Uttarakhand Dentists Registration Tribunals","West Bengal State Dental Council",
];

const councilsForCategory = (cat) => cat === "Medical" ? MEDICAL_COUNCILS : cat === "Dentistry" ? DENTAL_COUNCILS : [];
const showRegFields = (cat) => cat === "Medical" || cat === "Dentistry";
const STATES_INDIA = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh",
  "Jammu & Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand",
  "West Bengal","Other UT"
];
const STATUSES = ["Active","On Leave","Absconded","Relieved","Retired","Suspended"];
const ACCOMMODATION_TYPES = ["None (own arrangement)","Hostel Room (shared)","Hostel Room (single)","1 BHK","2 BHK","3 BHK","Guest House (transit)"];
const EMPLOYMENT_TYPES = ["Regular","Visiting Local","Visiting Non-Local","Consultant Recruited"];

const EXP_DESIGNATIONS = [
  "Junior Resident","Senior Resident","Tutor","Demonstrator",
  "Assistant Professor","Associate Professor","Professor",
  "Registrar","Consultant","Medical Officer","PhD Scholar","Fellowship Trainee","House Surgeon"
];
const EXP_INSTITUTION_TYPES = [
  "Government Medical College","Private Medical College","Deemed-to-be-University Medical College",
  "AIIMS / Institute of National Importance","District / Civil Hospital (non-teaching)",
  "Private Hospital / Clinic","Research Institute","Defence / Railway / ESI","International","Other"
];
const EXP_NATURE = ["Full-time","Part-time","Honorary"];
const EXP_REASONS = [
  "Career advancement","Completed contract","Personal reasons","Family relocation",
  "Higher studies","Resignation accepted","End of bond service","Other"
];
const EXP_DEPT_GROUPS = [
  { label: "Pre-Clinical", items: ["Anatomy","Physiology","Biochemistry","Pharmacology","Microbiology"] },
  { label: "Para-Clinical", items: ["Pathology","Forensic Medicine","Community Medicine"] },
  { label: "Clinical", items: [
    "General Medicine","General Surgery","Anaesthesiology","Radio-Diagnosis","Orthopaedics",
    "DVL (Dermatology, Venereology & Leprosy)","Paediatrics","OBG (Obstetrics & Gynaecology)",
    "Ophthalmology","ENT","Psychiatry","Cardiology","Neurology","Nephrology","Gastroenterology",
    "Cardiothoracic Surgery","Neurosurgery","Urology","Plastic Surgery","Dentistry",
    "Emergency Medicine","Geriatric Medicine"
  ]},
];
const emptyPosition = () => ({
  _uid: Date.now() + Math.random(),
  designation: "", department: "", institution: "", institution_type: "",
  country: "India", nmc_recognised: "Don't know", nature_of_employment: "Full-time",
  from_date: "", to_date: "", reason_for_leaving: "", reason_other: "",
  supervisor_name: "", supervisor_contact: "", notes: "",
});

const ACCOM_TYPES = ["Room (shared)","Room (single occupancy)","1 BHK","2 BHK","3 BHK","Cottage / Bungalow"];
const FURNISHED = ["Furnished","Semi-furnished","Unfurnished"];
const MESS_PLANS = ["Vegetarian","Non-Vegetarian","Both","Special / Dietary"];
const MEALS = ["Breakfast","Lunch","Dinner"];
const PETS_OPTIONS = ["None","Yes (dog/cat)","Yes (other)"];
const SHARED_TYPES = ["Room (shared)","Room (single occupancy)"];

const empty = {
  title: "Dr.",
  full_name: "",
  dob: "",
  blood_group: "",
  aadhaar: "",
  pan: "",
  whatsapp: "",
  whatsapp_verified: false,
  personal_email: "",
  personal_email_verified: false,
  official_email: "",
  // Correspondence address (structured)
  corr_house: "",
  corr_area: "",
  corr_post_office: "",
  corr_city: "",
  corr_state: "",
  corr_pin: "",
  // Permanent address
  permanent_address_same: true,
  perm_house: "",
  perm_area: "",
  perm_post_office: "",
  perm_city: "",
  perm_state: "",
  perm_pin: "",
  // File uploads (stored as base64 data URLs)
  photo_url: "",
  photo_name: "",
  pan_card_url: "",
  pan_card_name: "",
  aadhaar_card_url: "",
  aadhaar_card_name: "",
  appointment_letter_url: "",
  appointment_letter_name: "",
  appointment_letter_no: "",

  // ============ Bank, Salary & IDs ============
  ifsc_code: "",
  bank_name: "",
  bank_branch: "",
  account_number: "",
  bank_passbook_url: "",
  bank_passbook_name: "",
  monthly_salary: "",
  salary_effective_from: "",
  greyt_hr_id: "",
  aebas_id: "",
  id_card_status: "Pending",

  department: "",
  designation: "",
  track: "Medico",
  status: "Active",
  age: "",
  gender: "",
  marital_status: "",
  joined_at: "",
  employment_type: "",

  // ============ Qualifications · UG ============
  ug_category: "",
  ug_course: "",
  ug_college: "",
  ug_begin_year: "",
  ug_complete_year: "",
  ug_degree_url: "",
  ug_degree_name: "",
  ug_reg_date: "",
  ug_reg_cert_url: "",
  ug_reg_cert_name: "",
  ug_reg_number: "",
  ug_council: "",

  // Diploma
  has_diploma: false,
  diploma_category: "",
  diploma_title: "",
  diploma_year: "",
  diploma_institute: "",
  diploma_cert_url: "",
  diploma_cert_name: "",
  diploma_reg_date: "",
  diploma_reg_number: "",
  diploma_council: "",

  // ============ Qualifications · PG ============
  pg_category: "",
  pg_title: "",
  pg_college: "",
  pg_begin_year: "",
  pg_complete_year: "",
  pg_degree_url: "",
  pg_degree_name: "",
  pg_reg_date: "",
  pg_reg_cert_url: "",
  pg_reg_cert_name: "",
  pg_reg_number: "",
  pg_council: "",

  // ============ DNB ============
  has_dnb: false,
  dnb_institute: "",
  dnb_begin_year: "",
  dnb_complete_year: "",
  dnb_cert_url: "",
  dnb_cert_name: "",
  dnb_reg_date: "",
  dnb_reg_cert_url: "",
  dnb_reg_cert_name: "",
  dnb_reg_number: "",
  dnb_council: "",

  // ============ PhD ============
  has_phd: false,
  phd_category: "",
  phd_title: "",
  phd_college: "",
  phd_begin_year: "",
  phd_complete_year: "",
  phd_degree_url: "",
  phd_degree_name: "",
  phd_reg_date: "",
  phd_reg_cert_url: "",
  phd_reg_cert_name: "",
  phd_reg_number: "",
  phd_council: "",

  // ============ Super-Specialty ============
  has_ss: false,
  ss_category: "",
  ss_title: "",
  ss_college: "",
  ss_begin_year: "",
  ss_complete_year: "",
  ss_degree_url: "",
  ss_degree_name: "",
  ss_reg_date: "",
  ss_reg_cert_url: "",
  ss_reg_cert_name: "",
  ss_reg_number: "",
  ss_council: "",

  ug: "",
  pg: "",
  post_pg_years: "",
  years_in_role: "",
  publications: 0,
  met_done: false,
  bmr_done: false,
  notes: "",
  past_experience: [],
  // ============ Accommodation ============
  accom_needed: false,
  accom_type: "",
  accom_block: "",
  accom_quarter: "",
  accom_furnished: "Semi-furnished",
  accom_allotment_order: "",
  accom_allotment_date: "",
  accom_move_in: "",
  accom_rent: "0",
  accom_deposit: "",
  accom_mess: false,
  accom_mess_plan: "Both",
  accom_meals: ["Breakfast","Lunch","Dinner"],
  accom_mess_deduction: "",
  accom_family_count: "1",
  accom_pets: "None",
  accom_pets_detail: "",
  accom_notes: "",
};

const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function AddFaculty() {
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [depts, setDepts] = useState(DEPTS_FALLBACK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ifscLookup, setIfscLookup] = useState({ loading: false, error: "" });
  const [pinLookup, setPinLookup] = useState({ corr: { loading: false, error: "" }, perm: { loading: false, error: "" } });
  const [pinOffices, setPinOffices] = useState({ corr: [], perm: [] });
  const [compChecks, setCompChecks] = useState([]);
  const [compAcks, setCompAcks] = useState({});
  const [editLoaded, setEditLoaded] = useState(false);
  const navigate = useNavigate();

  // Load existing faculty data in edit mode
  useEffect(() => {
    if (!editId) return;
    api.get(`/faculty/${editId}`).then(({ data }) => {
      const merged = { ...empty };
      Object.keys(data).forEach(k => {
        if (k in merged) merged[k] = data[k] ?? merged[k];
        else merged[k] = data[k];
      });
      // Restore accommodation toggle
      if (data.accommodation) {
        merged.accom_needed = true;
        const a = data.accommodation;
        merged.accom_type = a.accommodation_type || "";
        merged.accom_block = a.block || "";
        merged.accom_quarter = a.quarter_number || "";
        merged.accom_furnished = a.furnished_status || "Semi-furnished";
        merged.accom_allotment_order = a.allotment_order_number || "";
        merged.accom_allotment_date = a.allotment_letter_date || "";
        merged.accom_move_in = a.move_in_date || "";
        merged.accom_rent = String(a.monthly_rent ?? "0");
        merged.accom_deposit = a.security_deposit != null ? String(a.security_deposit) : "";
        merged.accom_mess = !!a.mess_service;
        merged.accom_mess_plan = a.mess_plan || "Both";
        merged.accom_meals = a.meals_covered || ["Breakfast","Lunch","Dinner"];
        merged.accom_mess_deduction = a.mess_deduction != null ? String(a.mess_deduction) : "";
        merged.accom_family_count = String(a.family_members_count ?? "1");
        merged.accom_pets = a.pets || "None";
        merged.accom_pets_detail = a.pets_detail || "";
        merged.accom_notes = a.notes || "";
      }
      // Ensure arrays
      if (!Array.isArray(merged.past_experience)) merged.past_experience = [];
      if (!Array.isArray(merged.accom_meals)) merged.accom_meals = ["Breakfast","Lunch","Dinner"];
      // Ensure numeric-string fields
      ["age","post_pg_years","years_in_role","publications","monthly_salary"].forEach(k => {
        if (merged[k] != null) merged[k] = String(merged[k]);
      });
      setForm(merged);
      setEditLoaded(true);
    }).catch(() => { setError("Failed to load faculty data"); });
  }, [editId]);

  // Run compliance checks when entering the compliance step
  useEffect(() => {
    if (STEPS[step]?.key === "compliance") {
      setCompChecks(runComplianceChecks(form));
    }
  }, [step, form]);

  useEffect(() => {
    api.get("/reference/departments")
      .then(({data}) => setDepts(data.map(d => d.department)))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const stepKey = STEPS[step].key;

  // Past experience helpers
  const addPosition = () => setForm(prev => ({ ...prev, past_experience: [...prev.past_experience, emptyPosition()] }));
  const removePosition = (idx) => setForm(prev => ({ ...prev, past_experience: prev.past_experience.filter((_, i) => i !== idx) }));
  const setExp = (idx, k, v) => setForm(prev => {
    const arr = [...prev.past_experience];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...prev, past_experience: arr };
  });

  const IFSC_RE_FULL = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const lookupIfsc = async (code) => {
    if (!IFSC_RE_FULL.test(code)) return;
    setIfscLookup({ loading: true, error: "" });
    try {
      const res = await fetch(`https://ifsc.razorpay.com/${code}`);
      if (!res.ok) throw new Error("not found");
      const data = await res.json();
      setForm(prev => ({ ...prev, bank_name: data.BANK || prev.bank_name, bank_branch: data.BRANCH || prev.bank_branch }));
      setIfscLookup({ loading: false, error: "" });
    } catch {
      setIfscLookup({ loading: false, error: "IFSC not found" });
    }
  };

  const lookupPin = async (pin, prefix) => {
    if (!PIN_RE.test(pin)) {
      setPinOffices(prev => ({ ...prev, [prefix]: [] }));
      setForm(prev => ({ ...prev, [`${prefix}_city`]: "", [`${prefix}_state`]: "", [`${prefix}_post_office`]: "" }));
      return;
    }
    setPinLookup(prev => ({ ...prev, [prefix]: { loading: true, error: "" } }));
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (!data?.[0]?.PostOffice?.length) throw new Error("not found");
      const offices = data[0].PostOffice;
      const city = offices[0].District || "";
      const state = offices[0].State || "";
      const matchedState = STATES_INDIA.find(s => s.toLowerCase() === state.toLowerCase()) || "";
      const names = offices.map(o => o.Name).sort();
      setPinOffices(prev => ({ ...prev, [prefix]: names }));
      setForm(prev => ({
        ...prev,
        [`${prefix}_city`]: city,
        [`${prefix}_state`]: matchedState,
        [`${prefix}_post_office`]: names[0] || "",
      }));
      setPinLookup(prev => ({ ...prev, [prefix]: { loading: false, error: "" } }));
    } catch {
      setPinOffices(prev => ({ ...prev, [prefix]: [] }));
      setForm(prev => ({ ...prev, [`${prefix}_city`]: "", [`${prefix}_state`]: "", [`${prefix}_post_office`]: "" }));
      setPinLookup(prev => ({ ...prev, [prefix]: { loading: false, error: "PIN not found" } }));
    }
  };

  const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const AADHAAR_RE = /^[2-9][0-9]{11}$/;  // 12 digits, doesn't start with 0/1
  const PIN_RE = /^[1-9][0-9]{5}$/;        // 6 digits, doesn't start with 0

  const aadhaarDigits = (form.aadhaar || "").replace(/\s+/g, "");

  const canContinue = () => {
    if (stepKey === "identity") {
      const corrOk = form.corr_house.trim() && form.corr_area.trim() && form.corr_city.trim() && form.corr_state && PIN_RE.test(form.corr_pin);
      const permOk = form.permanent_address_same || (form.perm_house.trim() && form.perm_area.trim() && form.perm_city.trim() && form.perm_state && PIN_RE.test(form.perm_pin));
      return (
        form.full_name.trim().length > 0 &&
        !!form.gender &&
        !!form.marital_status &&
        !!form.blood_group &&
        PAN_RE.test(form.pan || "") &&
        AADHAAR_RE.test(aadhaarDigits) &&
        !!form.pan_card_url &&
        !!form.aadhaar_card_url &&
        form.whatsapp.trim().length > 0 &&
        form.personal_email.trim().length > 0 &&
        !!corrOk &&
        !!permOk
      );
    }
    if (stepKey === "position") {
      return !!form.joined_at && !!form.department && !!form.designation && !!form.employment_type && !!form.appointment_letter_url && form.appointment_letter_no.trim().length > 0;
    }
    if (stepKey === "bankids") {
      const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      return (
        IFSC_RE.test(form.ifsc_code || "") &&
        form.account_number.trim().length > 0 &&
        !!form.bank_passbook_url &&
        !!form.monthly_salary && Number(form.monthly_salary) > 0
      );
    }
    if (stepKey === "qualif") {
      const ugMbbs = form.ug_course === "MBBS" || form.ug_course === "BDS";
      const ugOk =
        !!form.ug_course && form.ug_college.trim() && form.ug_begin_year && form.ug_complete_year && !!form.ug_degree_url &&
        (!ugMbbs || (form.ug_reg_date && form.ug_reg_cert_url && form.ug_council));
      const diplomaOk = !form.has_diploma ||
        (form.diploma_title.trim() && form.diploma_year && form.diploma_institute.trim() && !!form.diploma_cert_url);
      const pgReg = form.pg_category === "Medical" || form.pg_category === "Dentistry";
      const pgOk =
        !!form.pg_category && form.pg_title.trim() && form.pg_college.trim() && form.pg_begin_year && form.pg_complete_year && !!form.pg_degree_url &&
        (!pgReg || (form.pg_reg_date && form.pg_reg_cert_url && form.pg_council));
      const dnbOk = !form.has_dnb ||
        (form.dnb_institute.trim() && form.dnb_begin_year && form.dnb_complete_year && !!form.dnb_cert_url &&
         form.dnb_reg_date && form.dnb_reg_cert_url && form.dnb_council);
      const phdReg = form.phd_category === "Medical" || form.phd_category === "Dentistry";
      const phdOk = !form.has_phd ||
        (form.phd_category && form.phd_title.trim() && form.phd_college.trim() && form.phd_begin_year && form.phd_complete_year && !!form.phd_degree_url &&
         (!phdReg || (form.phd_reg_date && form.phd_reg_cert_url && form.phd_council)));
      const ssReg = form.ss_category === "Medical" || form.ss_category === "Dentistry";
      const ssOk = !form.has_ss ||
        (form.ss_category && form.ss_title.trim() && form.ss_college.trim() && form.ss_begin_year && form.ss_complete_year && !!form.ss_degree_url &&
         (!ssReg || (form.ss_reg_date && form.ss_reg_cert_url && form.ss_council)));
      return ugOk && diplomaOk && pgOk && dnbOk && phdOk && ssOk;
    }
    if (stepKey === "experience") {
      return form.past_experience.every(p =>
        p.designation && p.institution.trim().length >= 3 && p.institution_type &&
        p.nature_of_employment && p.from_date && p.to_date &&
        new Date(p.to_date) > new Date(p.from_date) &&
        (!form.joined_at || new Date(p.to_date) <= new Date(form.joined_at)) &&
        (p.institution_type !== "International" || p.country.trim().length > 0)
      );
    }
    if (stepKey === "accommodation") {
      if (!form.accom_needed) return true; // skip path
      const messOk = !form.accom_mess || (form.accom_mess_plan && form.accom_meals.length > 0);
      return (
        !!form.accom_type && form.accom_block.trim().length > 0 && form.accom_quarter.trim().length > 0 &&
        !!form.accom_furnished && !!form.accom_allotment_date && !!form.accom_move_in &&
        new Date(form.accom_move_in) >= new Date(form.accom_allotment_date) &&
        form.accom_rent !== "" && Number(form.accom_rent) >= 0 &&
        Number(form.accom_family_count) >= 1 &&
        messOk
      );
    }
    if (stepKey === "compliance") {
      const hardFails = compChecks.filter(c => c.status === "Fail");
      const warnings = compChecks.filter(c => c.status === "Warning");
      const allWarnsAcked = warnings.every(w => compAcks[w.check_id]?.acknowledged);
      return hardFails.length === 0 && allWarnsAcked;
    }
    return true;
  };

  const handleFile = async (file, urlKey, nameKey, accept) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError(`${file.name} exceeds 2 MB. Compress or scan at lower DPI.`);
      return;
    }
    if (accept && !accept.some(t => file.type === t || file.type.startsWith(t.split("/")[0] + "/"))) {
      setError(`${file.name} is not an accepted file type.`);
      return;
    }
    setError("");
    const url = await readAsDataURL(file);
    setForm(prev => ({ ...prev, [urlKey]: url, [nameKey]: file.name }));
  };

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form };
      ["age","post_pg_years","years_in_role","publications","monthly_salary"].forEach(k => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      // Compose structured addresses into single-line strings for display
      payload.correspondence_address = [form.corr_house, form.corr_area, form.corr_post_office, form.corr_city, form.corr_state, form.corr_pin].filter(Boolean).join(" · ");
      if (form.permanent_address_same) {
        payload.permanent_address = payload.correspondence_address;
      } else {
        payload.permanent_address = [form.perm_house, form.perm_area, form.perm_post_office, form.perm_city, form.perm_state, form.perm_pin].filter(Boolean).join(" · ");
      }
      // Normalise Aadhaar to digits-only
      if (payload.aadhaar) payload.aadhaar = payload.aadhaar.replace(/\s+/g, "");
      // Clean past_experience: remove _uid, compose reason_for_leaving for "Other"
      if (payload.past_experience) {
        payload.past_experience = payload.past_experience.map(p => {
          const { _uid, reason_other, ...rest } = p;
          if (rest.reason_for_leaving === "Other" && reason_other) rest.reason_for_leaving = reason_other;
          Object.keys(rest).forEach(k => { if (rest[k] === "") delete rest[k]; });
          return rest;
        });
        if (payload.past_experience.length === 0) delete payload.past_experience;
      }
      // Clean accommodation: compose into structured object if needed
      if (payload.accom_needed) {
        payload.accommodation = {
          accommodation_type: form.accom_type,
          block: form.accom_block,
          quarter_number: form.accom_quarter,
          furnished_status: form.accom_furnished,
          allotment_order_number: form.accom_allotment_order || undefined,
          allotment_letter_date: form.accom_allotment_date,
          move_in_date: form.accom_move_in,
          monthly_rent: Number(form.accom_rent) || 0,
          security_deposit: form.accom_deposit ? Number(form.accom_deposit) : undefined,
          mess_service: form.accom_mess,
          mess_plan: form.accom_mess ? form.accom_mess_plan : undefined,
          meals_covered: form.accom_mess ? form.accom_meals : undefined,
          mess_deduction: form.accom_mess && form.accom_mess_deduction ? Number(form.accom_mess_deduction) : undefined,
          family_members_count: Number(form.accom_family_count) || 1,
          pets: form.accom_pets !== "None" ? form.accom_pets : undefined,
          pets_detail: form.accom_pets === "Yes (other)" ? form.accom_pets_detail : undefined,
          notes: form.accom_notes || undefined,
        };
      }
      // Remove internal accom_ prefixed fields from payload
      Object.keys(payload).forEach(k => { if (k.startsWith('accom_')) delete payload[k]; });
      // Attach compliance review
      const checks = runComplianceChecks(form);
      const passCt = checks.filter(c => c.status === "Pass").length;
      const warnCt = checks.filter(c => c.status === "Warning").length;
      const failCt = checks.filter(c => c.status === "Fail").length;
      payload.nmc_compliance_review = {
        context: "onboarding",
        overall_status: failCt > 0 ? "Non-Compliant" : warnCt > 0 ? "Compliant-with-warnings" : "Compliant",
        total_checks: checks.length, passed: passCt, warnings: warnCt, failures: failCt,
        check_results: checks, acknowledgments: Object.fromEntries(
          Object.entries(compAcks).filter(([,v]) => v.acknowledged).map(([k,v]) => [k, { acknowledged: v.acknowledged, note: v.note || "" }])
        ),
      };
      // strip empty strings
      Object.keys(payload).forEach(k => { if (payload[k] === "" || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k]; });
      if (isEdit) {
        await api.patch(`/faculty/${editId}`, payload);
        navigate(`/profile/${editId}`);
      } else {
        const { data } = await api.post("/faculty", payload);
        navigate(`/profile/${data.faculty_id}`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="view-add">
      <div className="topbar">
        <div>
          <h1 className="pg-title">{isEdit ? 'Edit Faculty' : 'Add New Faculty'}</h1>
          <div className="pg-sub">{isEdit ? `Editing ${form.title} ${form.full_name} · ${form.ssmch_id || ''}` : 'Operator wizard · 9 steps · human-centric onboarding'}</div>
        </div>
      </div>

      <div className="wizard">
        <aside className="wiz-side">
          <div className="wiz-side-title">Onboarding Steps</div>
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`wiz-step ${step === i ? 'active' : ''} ${i < step ? 'done' : ''}`}
              onClick={() => setStep(i)}
              data-testid={`wiz-step-${i+1}`}
            >
              <div className="num">{i < step ? '✓' : i+1}</div>
              <div>{s.title}</div>
            </div>
          ))}
        </aside>

        <section className="wiz-main">
          <div className="wiz-step-num">Step {step+1} of {STEPS.length}</div>
          <h2 className="wiz-step-title">{STEPS[step].title}</h2>
          <div className="wiz-step-sub">{STEPS[step].sub}</div>

          {stepKey === "identity" && (
            <>
              <div className="form-section">
                <div className="form-section-title">Name &amp; Date of Birth · Table A1</div>
                <div className="form-grid cols-3">
                  <div className="form-field">
                    <label className="form-label">Title <span className="req">*</span></label>
                    <select className="form-select" value={form.title} onChange={e => set('title', e.target.value)} data-testid="wiz-title">
                      <option>Dr.</option><option>Mr.</option><option>Mrs.</option><option>Ms.</option>
                    </select>
                  </div>
                  <div className="form-field span-2">
                    <label className="form-label">Full Name <span className="req">*</span><span className="help">as in Aadhaar · locked once verified</span></label>
                    <input className="form-input" placeholder="e.g. Anjali Sharma" value={form.full_name} onChange={e => {
                      set('full_name', e.target.value);
                      const parts = e.target.value.toLowerCase().trim().split(/\s+/);
                      if (parts.length >= 2) {
                        set('official_email', `${parts[0]}.${parts[parts.length-1]}@ssmch.in`);
                      }
                    }} data-testid="wiz-full-name"/>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" className="form-input" value={form.dob} onChange={e => {
                      set('dob', e.target.value);
                      if (e.target.value) {
                        const dob = new Date(e.target.value);
                        const age = Math.floor((Date.now() - dob.getTime()) / (365.25 * 86400 * 1000));
                        if (Number.isFinite(age) && age > 0 && age < 120) set('age', String(age));
                      }
                    }}/>
                    {form.dob && form.age && <div className="form-hint" data-testid="live-age">Live age: {form.age} yrs</div>}
                  </div>
                  <div className="form-field">
                    <label className="form-label">Age</label>
                    <input type="number" className="form-input mono" value={form.age} onChange={e => set('age', e.target.value)}/>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Blood Group <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={form.blood_group}
                      onChange={e => set('blood_group', e.target.value)}
                      data-testid="wiz-blood-group"
                    >
                      <option value="">Select</option>
                      {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Gender <span className="req">*</span></label>
                    <select className="form-select" value={form.gender} onChange={e => set('gender', e.target.value)} data-testid="wiz-gender">
                      <option value="">Select</option>
                      {GENDERS.map(g => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Marital Status <span className="req">*</span></label>
                    <select className="form-select" value={form.marital_status} onChange={e => set('marital_status', e.target.value)} data-testid="wiz-marital-status">
                      <option value="">Select</option>
                      {MARITAL_STATUSES.map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Government IDs &amp; Documents</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">Aadhaar Number <span className="req">*</span></label>
                    <input
                      className="form-input mono"
                      placeholder="0000 0000 0000"
                      maxLength={14}
                      value={form.aadhaar}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 12);
                        const grouped = raw.replace(/(\d{4})(?=\d)/g, "$1 ");
                        set('aadhaar', grouped);
                      }}
                      data-testid="wiz-aadhaar"
                    />
                    {aadhaarDigits && !AADHAAR_RE.test(aadhaarDigits) && (
                      <div className="form-hint" style={{color:'var(--bad)'}}>Must be 12 digits (cannot start with 0 or 1)</div>
                    )}
                  </div>
                  <div className="form-field">
                    <label className="form-label">PAN Number <span className="req">*</span></label>
                    <input
                      className="form-input mono"
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      style={{textTransform:'uppercase'}}
                      value={form.pan}
                      onChange={e => set('pan', e.target.value.toUpperCase())}
                      data-testid="wiz-pan"
                    />
                    {form.pan && !PAN_RE.test(form.pan) && (
                      <div className="form-hint" style={{color:'var(--bad)'}}>Format: 5 letters · 4 digits · 1 letter</div>
                    )}
                  </div>

                  {/* Aadhaar Card upload */}
                  <div className="form-field">
                    <label className="form-label">Aadhaar Card Upload <span className="req">*</span><span className="help">PDF / JPG / PNG · max 2 MB</span></label>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      className="form-input"
                      style={{padding:7}}
                      onChange={e => handleFile(e.target.files?.[0], 'aadhaar_card_url', 'aadhaar_card_name', ['application/pdf','image/jpeg','image/png'])}
                      data-testid="wiz-aadhaar-file"
                    />
                    {form.aadhaar_card_url && (
                      <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.aadhaar_card_name}</div>
                    )}
                  </div>

                  {/* PAN Card upload */}
                  <div className="form-field">
                    <label className="form-label">PAN Card Upload <span className="req">*</span><span className="help">PDF / JPG / PNG · max 2 MB</span></label>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      className="form-input"
                      style={{padding:7}}
                      onChange={e => handleFile(e.target.files?.[0], 'pan_card_url', 'pan_card_name', ['application/pdf','image/jpeg','image/png'])}
                      data-testid="wiz-pan-file"
                    />
                    {form.pan_card_url && (
                      <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.pan_card_name}</div>
                    )}
                  </div>

                  {/* Passport photo */}
                  <div className="form-field">
                    <label className="form-label">Passport-size Photograph<span className="help">JPG / PNG · max 2 MB</span></label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="form-input"
                      style={{padding:7}}
                      onChange={e => handleFile(e.target.files?.[0], 'photo_url', 'photo_name', ['image/jpeg','image/png'])}
                      data-testid="wiz-photo-file"
                    />
                  </div>
                  {form.photo_url && (
                    <div className="form-field">
                      <label className="form-label">Preview</label>
                      <img src={form.photo_url} alt="passport preview" style={{width:80, height:96, objectFit:'cover', border:'1px solid var(--line)', borderRadius:6}} data-testid="wiz-photo-preview"/>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Contact</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">
                      WhatsApp Number <span className="req">*</span>
                      <span className="help" style={{color:'var(--teal-ink)'}}>OTP verification · Phase 2</span>
                    </label>
                    <div style={{display:'flex', gap:8}}>
                      <input
                        className="form-input mono"
                        placeholder="+91 9876543210"
                        value={form.whatsapp}
                        onChange={e => set('whatsapp', e.target.value)}
                        style={{flex:1}}
                        data-testid="wiz-whatsapp"
                      />
                      <button type="button" className="btn" disabled title="OTP verification will be enabled in Phase 2" data-testid="wiz-whatsapp-otp">Verify OTP</button>
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">
                      Personal Email <span className="req">*</span>
                      <span className="help" style={{color:'var(--teal-ink)'}}>OTP verification · Phase 2</span>
                    </label>
                    <div style={{display:'flex', gap:8}}>
                      <input
                        className="form-input"
                        placeholder="name@example.com"
                        value={form.personal_email}
                        onChange={e => set('personal_email', e.target.value)}
                        style={{flex:1}}
                        data-testid="wiz-personal-email"
                      />
                      <button type="button" className="btn" disabled title="OTP verification will be enabled in Phase 2" data-testid="wiz-email-otp">Verify OTP</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Correspondence Address <span className="req">*</span></div>
                <div className="form-grid cols-3">
                  <div className="form-field span-2">
                    <label className="form-label">House / Flat / Street <span className="req">*</span></label>
                    <input className="form-input" placeholder="e.g. Flat 12, Tower B, MG Road" value={form.corr_house} onChange={e => set('corr_house', e.target.value)} data-testid="wiz-corr-house"/>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Area <span className="req">*</span></label>
                    <input className="form-input" placeholder="e.g. Wright Town" value={form.corr_area} onChange={e => set('corr_area', e.target.value)} data-testid="wiz-corr-area"/>
                  </div>
                  <div className="form-field">
                    <label className="form-label">PIN <span className="req">*</span></label>
                    <input
                      className="form-input mono"
                      placeholder="482001"
                      inputMode="numeric"
                      maxLength={6}
                      value={form.corr_pin}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9]/g, "").slice(0,6);
                        set('corr_pin', v);
                        lookupPin(v, 'corr');
                      }}
                      data-testid="wiz-corr-pin"
                    />
                    {pinLookup.corr.loading && (
                      <div className="form-hint" style={{color:'var(--text-2)'}}>Looking up PIN...</div>
                    )}
                    {pinLookup.corr.error && (
                      <div className="form-hint" style={{color:'var(--bad)'}}>{pinLookup.corr.error}</div>
                    )}
                    {form.corr_pin && !PIN_RE.test(form.corr_pin) && (
                      <div className="form-hint" style={{color:'var(--bad)'}}>6 digits, cannot start with 0</div>
                    )}
                  </div>
                  <div className="form-field">
                    <label className="form-label">Post Office {pinOffices.corr.length > 0 && <span className="req">*</span>}</label>
                    <select
                      className="form-select"
                      value={form.corr_post_office}
                      onChange={e => set('corr_post_office', e.target.value)}
                      disabled={pinOffices.corr.length === 0}
                      data-testid="wiz-corr-post-office"
                    >
                      {pinOffices.corr.length === 0 && <option value="">Enter PIN first</option>}
                      {pinOffices.corr.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">City / District</label>
                    <input className="form-input" value={form.corr_city} readOnly disabled style={{opacity:0.7}} data-testid="wiz-corr-city"/>
                  </div>
                  <div className="form-field">
                    <label className="form-label">State</label>
                    <input className="form-input" value={form.corr_state} readOnly disabled style={{opacity:0.7}} data-testid="wiz-corr-state"/>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Permanent Address</div>
                <label className="form-label" style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
                  <input
                    type="checkbox"
                    checked={form.permanent_address_same}
                    onChange={e => {
                      const same = e.target.checked;
                      set('permanent_address_same', same);
                      if (same) {
                        // mirror correspondence fields into permanent
                        setForm(prev => ({
                          ...prev,
                          permanent_address_same: true,
                          perm_house: prev.corr_house,
                          perm_area: prev.corr_area,
                          perm_post_office: prev.corr_post_office,
                          perm_city: prev.corr_city,
                          perm_state: prev.corr_state,
                          perm_pin: prev.corr_pin,
                        }));
                        setPinOffices(prev => ({ ...prev, perm: prev.corr }));
                      }
                    }}
                    data-testid="wiz-permanent-same"
                  />
                  Permanent address is same as correspondence address
                </label>
                {!form.permanent_address_same && (
                  <div className="form-grid cols-3">
                    <div className="form-field span-2">
                      <label className="form-label">House / Flat / Street <span className="req">*</span></label>
                      <input className="form-input" value={form.perm_house} onChange={e => set('perm_house', e.target.value)} data-testid="wiz-perm-house"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Area <span className="req">*</span></label>
                      <input className="form-input" value={form.perm_area} onChange={e => set('perm_area', e.target.value)} data-testid="wiz-perm-area"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PIN <span className="req">*</span></label>
                      <input
                        className="form-input mono"
                        inputMode="numeric"
                        maxLength={6}
                        value={form.perm_pin}
                        onChange={e => {
                          const v = e.target.value.replace(/[^0-9]/g, "").slice(0,6);
                          set('perm_pin', v);
                          lookupPin(v, 'perm');
                        }}
                        data-testid="wiz-perm-pin"
                      />
                      {pinLookup.perm.loading && (
                        <div className="form-hint" style={{color:'var(--text-2)'}}>Looking up PIN...</div>
                      )}
                      {pinLookup.perm.error && (
                        <div className="form-hint" style={{color:'var(--bad)'}}>{pinLookup.perm.error}</div>
                      )}
                      {form.perm_pin && !PIN_RE.test(form.perm_pin) && (
                        <div className="form-hint" style={{color:'var(--bad)'}}>6 digits, cannot start with 0</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Post Office {pinOffices.perm.length > 0 && <span className="req">*</span>}</label>
                      <select
                        className="form-select"
                        value={form.perm_post_office}
                        onChange={e => set('perm_post_office', e.target.value)}
                        disabled={pinOffices.perm.length === 0}
                        data-testid="wiz-perm-post-office"
                      >
                        {pinOffices.perm.length === 0 && <option value="">Enter PIN first</option>}
                        {pinOffices.perm.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">City / District</label>
                      <input className="form-input" value={form.perm_city} readOnly disabled style={{opacity:0.7}} data-testid="wiz-perm-city"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">State</label>
                      <input className="form-input" value={form.perm_state} readOnly disabled style={{opacity:0.7}} data-testid="wiz-perm-state"/>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {stepKey === "address" && (
            <div className="form-section">
              <div className="form-section-title">Emergency Contact &amp; Notes</div>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Emergency Contact Name</label>
                  <input className="form-input" placeholder="e.g. Spouse / Parent name" value={form.emergency_contact_name || ''} onChange={e => set('emergency_contact_name', e.target.value)} data-testid="wiz-emergency-name"/>
                </div>
                <div className="form-field">
                  <label className="form-label">Relation</label>
                  <select className="form-select" value={form.emergency_contact_relation || ''} onChange={e => set('emergency_contact_relation', e.target.value)}>
                    <option value="">Select</option>
                    {["Spouse","Father","Mother","Son","Daughter","Sibling","Other"].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Emergency Phone</label>
                  <input className="form-input mono" placeholder="+91 9876543210" value={form.emergency_contact_phone || ''} onChange={e => set('emergency_contact_phone', e.target.value)} data-testid="wiz-emergency-phone"/>
                </div>
                <div className="form-field span-2">
                  <label className="form-label">HR Notes</label>
                  <textarea className="form-input" placeholder="Anything HR should know" value={form.notes} onChange={e => set('notes', e.target.value)}/>
                </div>
              </div>
            </div>
          )}

          {stepKey === "qualif" && (() => {
            const pgReg = form.pg_category === "Medical" || form.pg_category === "Dentistry";
            const phdReg = form.phd_category === "Medical" || form.phd_category === "Dentistry";
            const ssReg = form.ss_category === "Medical" || form.ss_category === "Dentistry";
            const dur = (a, b) => {
              const av = parseInt(a, 10), bv = parseInt(b, 10);
              if (!Number.isFinite(av) || !Number.isFinite(bv) || bv < av) return "";
              const d = bv - av;
              return d === 0 ? "Under 1 yr" : `${d} year${d === 1 ? '' : 's'}`;
            };
            return (
              <>
                {/* ============== UG ============== */}
                <div className="form-section">
                  <div className="form-section-title">Under-Graduate (UG) Qualification · Table B1</div>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">UG Category <span className="req">*</span></label>
                      <select className="form-select" value={form.ug_category} onChange={e => set('ug_category', e.target.value)} data-testid="wiz-ug-category">
                        <option value="">Select</option>
                        {PG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">UG Course <span className="req">*</span></label>
                      <select className="form-select" value={form.ug_course} onChange={e => set('ug_course', e.target.value)} data-testid="wiz-ug-course">
                        <option value="">Select</option>
                        {UG_COURSES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">UG College <span className="req">*</span><span className="help">NMC recognized</span></label>
                      <CollegeSearch value={form.ug_college} onChange={v => set('ug_college', v)} courseType="MBBS" placeholder="Search NMC colleges..." testId="wiz-ug-college"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">UG Beginning Year <span className="req">*</span></label>
                      <input type="number" className="form-input mono" placeholder="2010" value={form.ug_begin_year} onChange={e => set('ug_begin_year', e.target.value)} data-testid="wiz-ug-begin"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">UG Completion Year <span className="req">*</span><span className="help">year of internship completion</span></label>
                      <input type="number" className="form-input mono" placeholder="2015" value={form.ug_complete_year} onChange={e => set('ug_complete_year', e.target.value)} data-testid="wiz-ug-complete"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">UG Course Duration <span className="help">derived</span></label>
                      <input className="form-input mono" readOnly value={dur(form.ug_begin_year, form.ug_complete_year) || "—"} data-testid="wiz-ug-duration"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">UG Degree (upload) <span className="req">*</span></label>
                      <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                        onChange={e => handleFile(e.target.files?.[0], 'ug_degree_url', 'ug_degree_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-ug-degree"/>
                      {form.ug_degree_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.ug_degree_name}</div>}
                    </div>
                    {showRegFields(form.ug_category) && <>
                      <div className="form-field">
                        <label className="form-label">Date of Registration (post UG) <span className="req">*</span></label>
                        <input type="date" className="form-input" value={form.ug_reg_date} onChange={e => set('ug_reg_date', e.target.value)} data-testid="wiz-ug-reg-date"/>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Certificate of Registration <span className="req">*</span></label>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                          onChange={e => handleFile(e.target.files?.[0], 'ug_reg_cert_url', 'ug_reg_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-ug-reg-cert"/>
                        {form.ug_reg_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.ug_reg_cert_name}</div>}
                      </div>
                      <div className="form-field">
                        <label className="form-label">Council for UG Registration <span className="req">*</span></label>
                        <select className="form-select" value={form.ug_council} onChange={e => set('ug_council', e.target.value)} data-testid="wiz-ug-council">
                          <option value="">Select</option>
                          {councilsForCategory(form.ug_category).map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Registration Number</label>
                        <input className="form-input mono" placeholder="e.g. MCI-12345/2018" value={form.ug_reg_number} onChange={e => set('ug_reg_number', e.target.value)} data-testid="wiz-ug-reg-number"/>
                      </div>
                    </>}
                  </div>
                </div>

                {/* ============== Diploma ============== */}
                <div className="form-section">
                  <div className="form-section-title">Diploma</div>
                  <label className="form-label" style={{display:'flex', gap:10, alignItems:'center', marginBottom:14}}>
                    <input type="checkbox" checked={form.has_diploma} onChange={e => set('has_diploma', e.target.checked)} data-testid="wiz-has-diploma"/>
                    Have you obtained a Diploma?
                  </label>
                  {form.has_diploma && (
                    <div className="form-grid">
                      <div className="form-field"><label className="form-label">Diploma Category <span className="req">*</span></label>
                        <select className="form-select" value={form.diploma_category || ''} onChange={e => set('diploma_category', e.target.value)} data-testid="wiz-diploma-category">
                          <option value="">Select</option>
                          {PG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-field"><label className="form-label">Diploma Title <span className="req">*</span></label>
                        <input className="form-input" value={form.diploma_title} onChange={e => set('diploma_title', e.target.value)} data-testid="wiz-diploma-title"/>
                      </div>
                      <div className="form-field"><label className="form-label">Year of Completion <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.diploma_year} onChange={e => set('diploma_year', e.target.value)} data-testid="wiz-diploma-year"/>
                      </div>
                      <div className="form-field"><label className="form-label">Institute <span className="req">*</span><span className="help">NMC recognized</span></label>
                        <CollegeSearch value={form.diploma_institute} onChange={v => set('diploma_institute', v)} courseType="Diploma" placeholder="Search NMC colleges..." testId="wiz-diploma-institute"/>
                      </div>
                      <div className="form-field"><label className="form-label">Diploma Certificate <span className="req">*</span></label>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                          onChange={e => handleFile(e.target.files?.[0], 'diploma_cert_url', 'diploma_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-diploma-cert"/>
                        {form.diploma_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.diploma_cert_name}</div>}
                      </div>
                      {showRegFields(form.diploma_category) && <>
                        <div className="form-field"><label className="form-label">Date of Registration <span className="req">*</span></label>
                          <input type="date" className="form-input" value={form.diploma_reg_date || ''} onChange={e => set('diploma_reg_date', e.target.value)} data-testid="wiz-diploma-reg-date"/>
                        </div>
                        <div className="form-field"><label className="form-label">Council for Registration <span className="req">*</span></label>
                          <select className="form-select" value={form.diploma_council || ''} onChange={e => set('diploma_council', e.target.value)} data-testid="wiz-diploma-council">
                            <option value="">Select</option>
                            {councilsForCategory(form.diploma_category).map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-field"><label className="form-label">Registration Number</label>
                          <input className="form-input mono" placeholder="e.g. MCI-12345/2018" value={form.diploma_reg_number || ''} onChange={e => set('diploma_reg_number', e.target.value)} data-testid="wiz-diploma-reg-number"/>
                        </div>
                      </>}
                    </div>
                  )}
                </div>

                {/* ============== PG ============== */}
                <div className="form-section">
                  <div className="form-section-title">Post-Graduate (PG) Qualification</div>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">PG Category <span className="req">*</span></label>
                      <select className="form-select" value={form.pg_category} onChange={e => set('pg_category', e.target.value)} data-testid="wiz-pg-category">
                        <option value="">Select</option>
                        {PG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PG Title <span className="req">*</span></label>
                      <input className="form-input" placeholder="e.g. MD General Medicine" value={form.pg_title} onChange={e => set('pg_title', e.target.value)} data-testid="wiz-pg-title"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PG College <span className="req">*</span><span className="help">NMC recognized</span></label>
                      <CollegeSearch value={form.pg_college} onChange={v => set('pg_college', v)} courseType="MD_MS" placeholder="Search NMC colleges..." testId="wiz-pg-college"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PG Beginning Year <span className="req">*</span></label>
                      <input type="number" className="form-input mono" value={form.pg_begin_year} onChange={e => set('pg_begin_year', e.target.value)} data-testid="wiz-pg-begin"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PG Completion Year <span className="req">*</span></label>
                      <input type="number" className="form-input mono" value={form.pg_complete_year} onChange={e => set('pg_complete_year', e.target.value)} data-testid="wiz-pg-complete"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PG Course Duration <span className="help">derived</span></label>
                      <input className="form-input mono" readOnly value={dur(form.pg_begin_year, form.pg_complete_year) || "—"}/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">PG Degree (upload) <span className="req">*</span></label>
                      <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                        onChange={e => handleFile(e.target.files?.[0], 'pg_degree_url', 'pg_degree_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-pg-degree"/>
                      {form.pg_degree_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.pg_degree_name}</div>}
                    </div>
                    {pgReg && <>
                      <div className="form-field">
                        <label className="form-label">Date of Registration (post PG) <span className="req">*</span></label>
                        <input type="date" className="form-input" value={form.pg_reg_date} onChange={e => set('pg_reg_date', e.target.value)} data-testid="wiz-pg-reg-date"/>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Certificate of Registration <span className="req">*</span></label>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                          onChange={e => handleFile(e.target.files?.[0], 'pg_reg_cert_url', 'pg_reg_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-pg-reg-cert"/>
                        {form.pg_reg_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.pg_reg_cert_name}</div>}
                      </div>
                      <div className="form-field">
                        <label className="form-label">Council for PG Registration <span className="req">*</span></label>
                        <select className="form-select" value={form.pg_council} onChange={e => set('pg_council', e.target.value)} data-testid="wiz-pg-council">
                          <option value="">Select</option>
                          {councilsForCategory(form.pg_category).map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Registration Number</label>
                        <input className="form-input mono" placeholder="e.g. MCI-12345/2018" value={form.pg_reg_number} onChange={e => set('pg_reg_number', e.target.value)} data-testid="wiz-pg-reg-number"/>
                      </div>
                    </>}
                  </div>
                </div>

                {/* ============== DNB ============== */}
                <div className="form-section">
                  <div className="form-section-title">DNB (Diplomate of National Board)</div>
                  <label className="form-label" style={{display:'flex', gap:10, alignItems:'center', marginBottom:14}}>
                    <input type="checkbox" checked={form.has_dnb} onChange={e => set('has_dnb', e.target.checked)} data-testid="wiz-has-dnb"/>
                    Have you completed your DNB?
                  </label>
                  {form.has_dnb && (
                    <div className="form-grid">
                      <div className="form-field"><label className="form-label">DNB Category <span className="req">*</span></label>
                        <select className="form-select" value={form.dnb_category || ''} onChange={e => set('dnb_category', e.target.value)} data-testid="wiz-dnb-category">
                          <option value="">Select</option>
                          {PG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-field"><label className="form-label">DNB Institute <span className="req">*</span><span className="help">NMC recognized</span></label>
                        <CollegeSearch value={form.dnb_institute} onChange={v => set('dnb_institute', v)} courseType="" placeholder="Search NMC colleges..." testId="wiz-dnb-institute"/>
                      </div>
                      <div className="form-field"><label className="form-label">DNB Beginning Year <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.dnb_begin_year} onChange={e => set('dnb_begin_year', e.target.value)} data-testid="wiz-dnb-begin"/>
                      </div>
                      <div className="form-field"><label className="form-label">DNB Completion Year <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.dnb_complete_year} onChange={e => set('dnb_complete_year', e.target.value)} data-testid="wiz-dnb-complete"/>
                      </div>
                      <div className="form-field"><label className="form-label">DNB Duration <span className="help">derived</span></label>
                        <input className="form-input mono" readOnly value={dur(form.dnb_begin_year, form.dnb_complete_year) || "—"}/>
                      </div>
                      <div className="form-field"><label className="form-label">DNB Certificate (upload) <span className="req">*</span></label>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                          onChange={e => handleFile(e.target.files?.[0], 'dnb_cert_url', 'dnb_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-dnb-cert"/>
                        {form.dnb_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.dnb_cert_name}</div>}
                      </div>
                      {showRegFields(form.dnb_category) && <>
                        <div className="form-field"><label className="form-label">Date of Registration (post DNB) <span className="req">*</span></label>
                          <input type="date" className="form-input" value={form.dnb_reg_date} onChange={e => set('dnb_reg_date', e.target.value)} data-testid="wiz-dnb-reg-date"/>
                        </div>
                        <div className="form-field"><label className="form-label">Certificate of Registration <span className="req">*</span></label>
                          <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                            onChange={e => handleFile(e.target.files?.[0], 'dnb_reg_cert_url', 'dnb_reg_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-dnb-reg-cert"/>
                          {form.dnb_reg_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.dnb_reg_cert_name}</div>}
                        </div>
                        <div className="form-field"><label className="form-label">Council for DNB Registration <span className="req">*</span></label>
                          <select className="form-select" value={form.dnb_council} onChange={e => set('dnb_council', e.target.value)} data-testid="wiz-dnb-council">
                            <option value="">Select</option>
                            {councilsForCategory(form.dnb_category).map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-field"><label className="form-label">Registration Number</label>
                          <input className="form-input mono" placeholder="e.g. MCI-12345/2018" value={form.dnb_reg_number || ''} onChange={e => set('dnb_reg_number', e.target.value)} data-testid="wiz-dnb-reg-number"/>
                        </div>
                      </>}
                    </div>
                  )}
                </div>

                {/* ============== PhD ============== */}
                <div className="form-section">
                  <div className="form-section-title">PhD</div>
                  <label className="form-label" style={{display:'flex', gap:10, alignItems:'center', marginBottom:14}}>
                    <input type="checkbox" checked={form.has_phd} onChange={e => set('has_phd', e.target.checked)} data-testid="wiz-has-phd"/>
                    Have you completed your PhD?
                  </label>
                  {form.has_phd && (
                    <div className="form-grid">
                      <div className="form-field"><label className="form-label">PhD Category <span className="req">*</span></label>
                        <select className="form-select" value={form.phd_category} onChange={e => set('phd_category', e.target.value)} data-testid="wiz-phd-category">
                          <option value="">Select</option>
                          {PG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-field"><label className="form-label">PhD Title <span className="req">*</span></label>
                        <input className="form-input" value={form.phd_title} onChange={e => set('phd_title', e.target.value)} data-testid="wiz-phd-title"/>
                      </div>
                      <div className="form-field"><label className="form-label">PhD College <span className="req">*</span><span className="help">NMC recognized</span></label>
                        <CollegeSearch value={form.phd_college} onChange={v => set('phd_college', v)} courseType="DM_MCh" placeholder="Search NMC colleges..." testId="wiz-phd-college"/>
                      </div>
                      <div className="form-field"><label className="form-label">PhD Beginning Year <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.phd_begin_year} onChange={e => set('phd_begin_year', e.target.value)} data-testid="wiz-phd-begin"/>
                      </div>
                      <div className="form-field"><label className="form-label">PhD Completion Year <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.phd_complete_year} onChange={e => set('phd_complete_year', e.target.value)} data-testid="wiz-phd-complete"/>
                      </div>
                      <div className="form-field"><label className="form-label">PhD Duration <span className="help">derived</span></label>
                        <input className="form-input mono" readOnly value={dur(form.phd_begin_year, form.phd_complete_year) || "—"}/>
                      </div>
                      <div className="form-field"><label className="form-label">PhD Degree (upload) <span className="req">*</span></label>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                          onChange={e => handleFile(e.target.files?.[0], 'phd_degree_url', 'phd_degree_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-phd-degree"/>
                        {form.phd_degree_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.phd_degree_name}</div>}
                      </div>
                      {phdReg && <>
                        <div className="form-field"><label className="form-label">Date of Registration (post PhD) <span className="req">*</span></label>
                          <input type="date" className="form-input" value={form.phd_reg_date} onChange={e => set('phd_reg_date', e.target.value)} data-testid="wiz-phd-reg-date"/>
                        </div>
                        <div className="form-field"><label className="form-label">Certificate of Registration <span className="req">*</span></label>
                          <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                            onChange={e => handleFile(e.target.files?.[0], 'phd_reg_cert_url', 'phd_reg_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-phd-reg-cert"/>
                          {form.phd_reg_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.phd_reg_cert_name}</div>}
                        </div>
                        <div className="form-field"><label className="form-label">Council for PhD Registration <span className="req">*</span></label>
                          <select className="form-select" value={form.phd_council} onChange={e => set('phd_council', e.target.value)} data-testid="wiz-phd-council">
                            <option value="">Select</option>
                            {councilsForCategory(form.phd_category).map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-field"><label className="form-label">Registration Number</label>
                          <input className="form-input mono" placeholder="e.g. MCI-12345/2018" value={form.phd_reg_number || ''} onChange={e => set('phd_reg_number', e.target.value)} data-testid="wiz-phd-reg-number"/>
                        </div>
                      </>}
                    </div>
                  )}
                </div>

                {/* Super-Specialty */}
                <div style={{borderTop:'1px solid var(--line-soft)',paddingTop:14,marginTop:14}}>
                  <label style={{display:'flex',gap:8,alignItems:'center',fontSize:13,cursor:'pointer',marginBottom:10}}>
                    <input type="checkbox" checked={form.has_ss} onChange={e => set('has_ss', e.target.checked)} data-testid="wiz-has-ss"/>
                    Have you completed a Super-Specialty (DM / MCh)?
                  </label>
                  {form.has_ss && (
                    <div className="form-grid">
                      <div className="form-field"><label className="form-label">Category <span className="req">*</span></label>
                        <select className="form-select" value={form.ss_category} onChange={e => set('ss_category', e.target.value)} data-testid="wiz-ss-category">
                          <option value="">Select</option>
                          {PG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-field"><label className="form-label">Super-Specialty Title <span className="req">*</span></label>
                        <input className="form-input" placeholder="e.g. DM Cardiology, MCh Neurosurgery" value={form.ss_title} onChange={e => set('ss_title', e.target.value)} data-testid="wiz-ss-title"/>
                      </div>
                      <div className="form-field"><label className="form-label">College <span className="req">*</span><span className="help">NMC recognized</span></label>
                        <CollegeSearch value={form.ss_college} onChange={v => set('ss_college', v)} courseType="DM_MCh" placeholder="Search NMC colleges..." testId="wiz-ss-college"/>
                      </div>
                      <div className="form-field"><label className="form-label">Beginning Year <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.ss_begin_year} onChange={e => set('ss_begin_year', e.target.value)} data-testid="wiz-ss-begin"/>
                      </div>
                      <div className="form-field"><label className="form-label">Completion Year <span className="req">*</span></label>
                        <input type="number" className="form-input mono" value={form.ss_complete_year} onChange={e => set('ss_complete_year', e.target.value)} data-testid="wiz-ss-complete"/>
                      </div>
                      <div className="form-field"><label className="form-label">Duration <span className="help">derived</span></label>
                        <input className="form-input mono" readOnly value={dur(form.ss_begin_year, form.ss_complete_year) || "—"}/>
                      </div>
                      <div className="form-field"><label className="form-label">Degree Certificate (upload) <span className="req">*</span></label>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                          onChange={e => handleFile(e.target.files?.[0], 'ss_degree_url', 'ss_degree_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-ss-degree"/>
                        {form.ss_degree_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.ss_degree_name}</div>}
                      </div>
                      {ssReg && <>
                        <div className="form-field"><label className="form-label">Date of Registration <span className="req">*</span></label>
                          <input type="date" className="form-input" value={form.ss_reg_date} onChange={e => set('ss_reg_date', e.target.value)} data-testid="wiz-ss-reg-date"/>
                        </div>
                        <div className="form-field"><label className="form-label">Certificate of Registration <span className="req">*</span></label>
                          <input type="file" accept="application/pdf,image/jpeg,image/png" className="form-input" style={{padding:7}}
                            onChange={e => handleFile(e.target.files?.[0], 'ss_reg_cert_url', 'ss_reg_cert_name', ['application/pdf','image/jpeg','image/png'])} data-testid="wiz-ss-reg-cert"/>
                          {form.ss_reg_cert_url && <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.ss_reg_cert_name}</div>}
                        </div>
                        <div className="form-field"><label className="form-label">Council for Registration <span className="req">*</span></label>
                          <select className="form-select" value={form.ss_council} onChange={e => set('ss_council', e.target.value)} data-testid="wiz-ss-council">
                            <option value="">Select</option>
                            {councilsForCategory(form.ss_category).map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-field"><label className="form-label">Registration Number</label>
                          <input className="form-input mono" placeholder="e.g. MCI-12345/2018" value={form.ss_reg_number || ''} onChange={e => set('ss_reg_number', e.target.value)} data-testid="wiz-ss-reg-number"/>
                        </div>
                      </>}
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {stepKey === "position" && (
            <div className="form-section">
              <div className="form-section-title">Position at SSMCH · Table B2</div>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Date of Joining <span className="req">*</span></label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.joined_at}
                    onChange={e => set('joined_at', e.target.value)}
                    data-testid="wiz-joined-at"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Department <span className="req">*</span></label>
                  <select
                    className="form-select"
                    value={form.department}
                    onChange={e => set('department', e.target.value)}
                    data-testid="wiz-dept"
                  >
                    <option value="">Select department</option>
                    {DEPT_GROUPS.map(g => (
                      <optgroup key={g.label} label={g.label}>
                        {g.items.map(d => <option key={d} value={d}>{d}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Designation <span className="req">*</span></label>
                  <select
                    className="form-select"
                    value={form.designation}
                    onChange={e => set('designation', e.target.value)}
                    data-testid="wiz-desig"
                  >
                    <option value="">Select designation</option>
                    {DESIGS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">
                    Nature of Employment <span className="req">*</span>
                    <span className="help" style={{color:'var(--bad)'}}>Confidential · Table G</span>
                  </label>
                  <select
                    className="form-select"
                    value={form.employment_type}
                    onChange={e => set('employment_type', e.target.value)}
                    data-testid="wiz-employment-type"
                  >
                    <option value="">Select</option>
                    {EMPLOYMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-field span-2">
                  <label className="form-label">Appointment Letter <span className="req">*</span><span className="help">PDF / JPG / PNG · max 2 MB</span></label>
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    className="form-input"
                    style={{padding:7}}
                    onChange={e => handleFile(e.target.files?.[0], 'appointment_letter_url', 'appointment_letter_name', ['application/pdf','image/jpeg','image/png'])}
                    data-testid="wiz-appointment-letter"
                  />
                  {form.appointment_letter_url && (
                    <div className="form-hint" style={{color:'var(--ok)'}}>✓ {form.appointment_letter_name}</div>
                  )}
                </div>
                <div className="form-field span-2">
                  <label className="form-label">Appointment Letter Number <span className="req">*</span></label>
                  <input
                    className="form-input mono"
                    placeholder="e.g. SSMCH/HR/APP/2026/0123"
                    value={form.appointment_letter_no}
                    onChange={e => set('appointment_letter_no', e.target.value)}
                    data-testid="wiz-appointment-no"
                  />
                </div>
              </div>
            </div>
          )}

          {stepKey === "bankids" && (
            <>
              <div className="form-section">
                <div className="form-section-title">Bank Account Details · Table G</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">IFSC Code <span className="req">*</span></label>
                    <input
                      className="form-input mono"
                      placeholder="e.g. SBIN0001234"
                      maxLength={11}
                      style={{textTransform:'uppercase'}}
                      value={form.ifsc_code}
                      onChange={e => {
                        const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
                        set('ifsc_code', v);
                        if (/^[A-Z]{4}0[A-Z0-9]{6}$/.test(v)) lookupIfsc(v);
                      }}
                      data-testid="wiz-ifsc"
                    />
                    {ifscLookup.loading && (
                      <div className="form-hint" style={{color:'var(--text-2)'}}>Looking up IFSC...</div>
                    )}
                    {ifscLookup.error && (
                      <div className="form-hint" style={{color:'var(--bad)'}}>{ifscLookup.error}</div>
                    )}
                    {!ifscLookup.loading && !ifscLookup.error && form.ifsc_code && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code) && form.bank_name && (
                      <div className="form-hint" style={{color:'var(--ok)'}}>&#10003; {form.bank_name}{form.bank_branch ? `, ${form.bank_branch}` : ''}</div>
                    )}
                    {form.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code) && (
                      <div className="form-hint" style={{color:'var(--bad)'}}>Format: 4 letters · 0 · 6 alphanumeric (e.g. SBIN0001234)</div>
                    )}
                  </div>
                  <div className="form-field">
                    <label className="form-label">Bank Name</label>
                    <input
                      className="form-input"
                      placeholder="e.g. State Bank of India"
                      value={form.bank_name}
                      onChange={e => set('bank_name', e.target.value)}
                      data-testid="wiz-bank-name"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Branch Name</label>
                    <input
                      className="form-input"
                      placeholder="e.g. Chhindwara Main"
                      value={form.bank_branch}
                      onChange={e => set('bank_branch', e.target.value)}
                      data-testid="wiz-bank-branch"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Account Number <span className="req">*</span></label>
                    <input
                      className="form-input mono"
                      placeholder="e.g. 30281234567"
                      value={form.account_number}
                      onChange={e => set('account_number', e.target.value.replace(/[^0-9]/g, ''))}
                      data-testid="wiz-account-number"
                    />
                  </div>
                  <div className="form-field span-2">
                    <label className="form-label">Bank Passbook / Cheque <span className="req">*</span><span className="help">PDF / JPG / PNG · max 2 MB</span></label>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      className="form-input"
                      style={{padding:7}}
                      onChange={e => handleFile(e.target.files?.[0], 'bank_passbook_url', 'bank_passbook_name', ['application/pdf','image/jpeg','image/png'])}
                      data-testid="wiz-bank-passbook"
                    />
                    {form.bank_passbook_url && (
                      <div className="form-hint" style={{color:'var(--ok)'}}>&#10003; {form.bank_passbook_name}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Salary &amp; Payroll</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">Monthly Gross Salary <span className="req">*</span></label>
                    <div style={{position:'relative'}}>
                      <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>&#8377;</span>
                      <input
                        type="number"
                        className="form-input mono"
                        style={{paddingLeft:26}}
                        placeholder="e.g. 125000"
                        min="0"
                        value={form.monthly_salary}
                        onChange={e => set('monthly_salary', e.target.value)}
                        data-testid="wiz-monthly-salary"
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Salary Effective From</label>
                    <input
                      type="date"
                      className="form-input"
                      value={form.salary_effective_from}
                      onChange={e => set('salary_effective_from', e.target.value)}
                      data-testid="wiz-salary-from"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">SSMCH-Issued Credentials</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">GreytHR ID</label>
                    <input
                      className="form-input mono"
                      placeholder="e.g. SSMCH-0456"
                      value={form.greyt_hr_id}
                      onChange={e => set('greyt_hr_id', e.target.value)}
                      data-testid="wiz-greythr-id"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">AEBAS ID</label>
                    <input
                      className="form-input mono"
                      placeholder="e.g. AEB-9012"
                      value={form.aebas_id}
                      onChange={e => set('aebas_id', e.target.value)}
                      data-testid="wiz-aebas-id"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Official Email</label>
                    <input
                      className="form-input"
                      placeholder="auto-generated from name"
                      value={form.official_email}
                      onChange={e => set('official_email', e.target.value)}
                      data-testid="wiz-official-email"
                    />
                    <div className="form-hint">Auto-filled from Full Name; editable</div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">ID Card Status</label>
                    <select
                      className="form-select"
                      value={form.id_card_status}
                      onChange={e => set('id_card_status', e.target.value)}
                      data-testid="wiz-id-card-status"
                    >
                      <option>Pending</option>
                      <option>Issued</option>
                      <option>Returned</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {stepKey === "experience" && (
            <>
              {form.past_experience.length === 0 && (
                <div className="form-section" style={{textAlign:'center',padding:'40px 20px',color:'var(--text-3)',fontSize:13}}>
                  No previous positions added. Add one below, or click Continue to skip if this is the faculty's first teaching post.
                </div>
              )}

              {form.past_experience.map((pos, idx) => {
                const fromAfterTo = pos.from_date && pos.to_date && new Date(pos.to_date) <= new Date(pos.from_date);
                const afterJoin = pos.to_date && form.joined_at && new Date(pos.to_date) > new Date(form.joined_at);
                const isMedCollege = ["Government Medical College","Private Medical College","Deemed-to-be-University Medical College","AIIMS / Institute of National Importance"].includes(pos.institution_type);
                return (
                <div key={pos._uid} className="form-section" style={{position:'relative',border:'1px solid var(--border)',borderRadius:12,padding:18,marginBottom:16}} data-testid={`exp-card-${idx}`}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <div className="mono" style={{fontSize:11,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:1}}>Position {idx+1}</div>
                    <button type="button" onClick={() => removePosition(idx)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'var(--text-3)',padding:'2px 6px',borderRadius:4}} data-testid={`exp-remove-${idx}`}
                      onMouseEnter={e => {e.target.style.color='var(--bad)';e.target.style.background='rgba(248,113,113,0.1)'}}
                      onMouseLeave={e => {e.target.style.color='var(--text-3)';e.target.style.background='none'}}
                    >&times;</button>
                  </div>

                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Designation <span className="req">*</span></label>
                      <select className="form-select" value={pos.designation} onChange={e => setExp(idx,'designation',e.target.value)} data-testid={`exp-desig-${idx}`}>
                        <option value="">Select designation</option>
                        {EXP_DESIGNATIONS.map(d => <option key={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Department / Specialty</label>
                      <select className="form-select" value={pos.department} onChange={e => setExp(idx,'department',e.target.value)} data-testid={`exp-dept-${idx}`}>
                        <option value="">Select</option>
                        {EXP_DEPT_GROUPS.map(g => (
                          <optgroup key={g.label} label={g.label}>
                            {g.items.map(d => <option key={d} value={d}>{d}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Institution <span className="req">*</span></label>
                      <input className="form-input" placeholder="e.g. GMC Jabalpur" minLength={3} value={pos.institution} onChange={e => setExp(idx,'institution',e.target.value)} data-testid={`exp-inst-${idx}`}/>
                      {pos.institution && pos.institution.trim().length < 3 && (
                        <div className="form-hint" style={{color:'var(--bad)'}}>Minimum 3 characters</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Institution Type <span className="req">*</span></label>
                      <select className="form-select" value={pos.institution_type} onChange={e => setExp(idx,'institution_type',e.target.value)} data-testid={`exp-insttype-${idx}`}>
                        <option value="">Select</option>
                        {EXP_INSTITUTION_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Country {pos.institution_type === 'International' && <span className="req">*</span>}</label>
                      <input className="form-input" value={pos.country} onChange={e => setExp(idx,'country',e.target.value)} data-testid={`exp-country-${idx}`}/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">NMC-recognised during tenure? <span className="req">*</span></label>
                      <div style={{display:'flex',gap:0,marginTop:4,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
                        {["Yes","No","Don't know"].map(opt => (
                          <button type="button" key={opt} onClick={() => setExp(idx,'nmc_recognised',opt)}
                            style={{flex:1,padding:'7px 4px',fontSize:12,fontWeight:pos.nmc_recognised===opt?600:400,border:'none',cursor:'pointer',
                              background:pos.nmc_recognised===opt?(opt==='Yes'?'var(--ok)':opt==='No'?'var(--bad)':'var(--text-3)'):'var(--bg)',
                              color:pos.nmc_recognised===opt?'#fff':'var(--text-2)'}}
                            data-testid={`exp-nmc-${opt.replace(/\s/g,'').toLowerCase()}-${idx}`}
                          >{opt}</button>
                        ))}
                      </div>
                      <div className="form-hint">Was this institution NMC-recognised while the faculty worked there?</div>
                      {isMedCollege && pos.nmc_recognised === "Don't know" && (
                        <div className="form-hint" style={{color:'var(--warn)'}}>Medical colleges should specify Yes or No</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Nature of Employment <span className="req">*</span></label>
                      <select className="form-select" value={pos.nature_of_employment} onChange={e => setExp(idx,'nature_of_employment',e.target.value)} data-testid={`exp-nature-${idx}`}>
                        {EXP_NATURE.map(n => <option key={n}>{n}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">From Date <span className="req">*</span></label>
                      <input type="date" className="form-input" value={pos.from_date} max={new Date().toISOString().slice(0,10)} onChange={e => setExp(idx,'from_date',e.target.value)} data-testid={`exp-from-${idx}`}/>
                      {pos.from_date && form.ug_complete_year && Number(pos.from_date.slice(0,4)) < Number(form.ug_complete_year) && (
                        <div className="form-hint" style={{color:'var(--warn)'}}>This position predates the MBBS year. Is the date correct?</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">To Date <span className="req">*</span></label>
                      <input type="date" className="form-input" value={pos.to_date} onChange={e => setExp(idx,'to_date',e.target.value)} data-testid={`exp-to-${idx}`}/>
                      {fromAfterTo && (
                        <div className="form-hint" style={{color:'var(--bad)'}}>To Date must be after From Date</div>
                      )}
                      {afterJoin && (
                        <div className="form-hint" style={{color:'var(--bad)'}}>To Date cannot be after the SSMCH Date of Joining ({form.joined_at})</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Reason for Leaving</label>
                      <select className="form-select" value={pos.reason_for_leaving} onChange={e => setExp(idx,'reason_for_leaving',e.target.value)} data-testid={`exp-reason-${idx}`}>
                        <option value="">Select</option>
                        {EXP_REASONS.map(r => <option key={r}>{r}</option>)}
                      </select>
                      {pos.reason_for_leaving === "Other" && (
                        <input className="form-input" placeholder="Specify reason" style={{marginTop:6}} value={pos.reason_other} onChange={e => setExp(idx,'reason_other',e.target.value)} data-testid={`exp-reason-other-${idx}`}/>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Reporting Supervisor — Name</label>
                      <input className="form-input" placeholder="Dr. Name of Head / Reporting Officer" value={pos.supervisor_name} onChange={e => setExp(idx,'supervisor_name',e.target.value)} data-testid={`exp-supname-${idx}`}/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Reporting Supervisor — Contact</label>
                      <input className="form-input" placeholder="Phone or email" value={pos.supervisor_contact} onChange={e => setExp(idx,'supervisor_contact',e.target.value)} data-testid={`exp-supcontact-${idx}`}/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Experience Certificate</label>
                      <button type="button" disabled className="form-input" style={{opacity:0.5,cursor:'not-allowed',textAlign:'left',fontSize:12}} title="Document uploads will be enabled in a later phase">+ Upload (deferred)</button>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Relieving Letter</label>
                      <button type="button" disabled className="form-input" style={{opacity:0.5,cursor:'not-allowed',textAlign:'left',fontSize:12}} title="Document uploads will be enabled in a later phase">+ Upload (deferred)</button>
                    </div>
                    <div className="form-field span-2">
                      <label className="form-label">Notes / Context</label>
                      <textarea className="form-input" placeholder="Anything worth recording — sabbatical periods, internal promotions, special arrangements, etc." style={{minHeight:64}} value={pos.notes} onChange={e => setExp(idx,'notes',e.target.value)} data-testid={`exp-notes-${idx}`}/>
                    </div>
                  </div>
                </div>
              );})}

              <button type="button" onClick={addPosition} style={{width:'100%',padding:'14px',border:'1.5px dashed var(--border)',borderRadius:10,background:'transparent',color:'var(--accent)',cursor:'pointer',fontSize:13,fontWeight:500,marginBottom:16}} data-testid="exp-add-position"
                onMouseEnter={e => e.target.style.background='rgba(107,156,255,0.06)'}
                onMouseLeave={e => e.target.style.background='transparent'}
              >+ Add Another Position</button>

              <div style={{fontSize:11,color:'var(--text-3)',marginBottom:8}}>
                Tip: if the faculty was promoted within the same institution, add a separate row for each designation with consecutive From / To dates.
              </div>

              <div style={{background:'rgba(251,191,36,0.08)',border:'1px solid rgba(251,191,36,0.3)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-2)',lineHeight:1.6}}>
                <div style={{fontWeight:600,marginBottom:4,color:'var(--warn)'}}>NMC Experience Rules</div>
                <div>Per NMC norms, only <strong>full-time</strong> teaching experience counts toward promotion criteria. Part-time and honorary positions are recorded but excluded from the eligibility engine.</div>
                <div style={{marginTop:4}}>Do not add an internship row — the MBBS completion year in Qualifications already accounts for it.</div>
                <div style={{marginTop:4}}>The position entered in Step 3 (Position at SSMCH) is recorded automatically. Add only previous positions here.</div>
              </div>
            </>
          )}

          {stepKey === "accommodation" && (
            <>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',border:'1px solid var(--border)',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
                <div>
                  <div style={{fontWeight:500,fontSize:14}}>Faculty needs institutional accommodation</div>
                  <div style={{fontSize:11,color:'var(--text-3)',marginTop:2}}>Toggle on to record allotment details</div>
                </div>
                <label style={{position:'relative',display:'inline-block',width:42,height:22,cursor:'pointer',flexShrink:0}} data-testid="accom-toggle">
                  <input type="checkbox" checked={form.accom_needed} onChange={e => {
                    const on = e.target.checked;
                    set('accom_needed', on);
                    if (on && !form.accom_type) {
                      // set defaults
                    }
                  }} style={{opacity:0,width:0,height:0}}/>
                  <span style={{position:'absolute',inset:0,borderRadius:22,background:form.accom_needed?'#16a34a':'#cbd5e1',transition:'background 0.2s'}}>
                    <span style={{position:'absolute',top:2,left:form.accom_needed?22:2,width:18,height:18,borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 4px rgba(0,0,0,.25)'}}/>
                  </span>
                </label>
              </div>

              {!form.accom_needed && (
                <div style={{textAlign:'center',padding:'30px 20px',color:'var(--text-3)',fontSize:13}}>
                  Self-arranged. The faculty will manage their own accommodation. Click Continue to proceed.
                </div>
              )}

              {!form.accom_needed && (
                <div style={{background:'rgba(107,156,255,0.06)',border:'1px solid rgba(107,156,255,0.2)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-2)'}}>
                  Skipping this step. No accommodation row will be created for this faculty.
                </div>
              )}

              <div style={{overflow:'hidden',maxHeight:form.accom_needed?5000:0,opacity:form.accom_needed?1:0,transition:'max-height 0.3s ease, opacity 0.2s ease'}}>

                <div className="form-section">
                  <div className="form-section-title">Accommodation Details</div>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Accommodation Type <span className="req">*</span></label>
                      <select className="form-select" value={form.accom_type} onChange={e => {
                        const v = e.target.value;
                        set('accom_type', v);
                        if (SHARED_TYPES.includes(v)) set('accom_mess', true);
                        else set('accom_mess', false);
                      }} data-testid="accom-type">
                        <option value="">Select</option>
                        {ACCOM_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Block / Wing <span className="req">*</span></label>
                      <input className="form-input" placeholder="e.g. Block A, Faculty Quarters East Wing" value={form.accom_block} onChange={e => set('accom_block', e.target.value)} data-testid="accom-block"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Quarter / Flat Number <span className="req">*</span></label>
                      <input className="form-input" placeholder="e.g. 104, A-12, FQ-7" value={form.accom_quarter} onChange={e => set('accom_quarter', e.target.value)} data-testid="accom-quarter"/>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Furnished Status <span className="req">*</span></label>
                      <select className="form-select" value={form.accom_furnished} onChange={e => set('accom_furnished', e.target.value)} data-testid="accom-furnished">
                        {FURNISHED.map(f => <option key={f}>{f}</option>)}
                      </select>
                      <div className="form-hint">Furnished = beds, wardrobes, basic kitchen. Semi = beds and wardrobes only. Unfurnished = empty.</div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">Allotment</div>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Allotment Order Number</label>
                      <input className="form-input mono" placeholder="e.g. SSMCH/HRA/2026/045" value={form.accom_allotment_order} onChange={e => set('accom_allotment_order', e.target.value)} data-testid="accom-allotment-order"/>
                      <div className="form-hint">Reference number from the allotment letter, if available.</div>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Allotment Letter Date <span className="req">*</span></label>
                      <input type="date" className="form-input" max={new Date().toISOString().slice(0,10)} value={form.accom_allotment_date} onChange={e => set('accom_allotment_date', e.target.value)} data-testid="accom-allotment-date"/>
                      {form.accom_allotment_date && form.joined_at && new Date(form.accom_allotment_date) < new Date(form.joined_at) && (
                        <div className="form-hint" style={{color:'var(--warn)'}}>Allotment date is before date of joining. Confirm if intentional.</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Date of Moving In <span className="req">*</span></label>
                      <input type="date" className="form-input" max={new Date().toISOString().slice(0,10)} value={form.accom_move_in} onChange={e => set('accom_move_in', e.target.value)} data-testid="accom-move-in"/>
                      {form.accom_move_in && form.accom_allotment_date && new Date(form.accom_move_in) < new Date(form.accom_allotment_date) && (
                        <div className="form-hint" style={{color:'var(--bad)'}}>Move-in date cannot be before the allotment letter date</div>
                      )}
                      {form.accom_move_in && form.joined_at && new Date(form.accom_move_in) < new Date(form.joined_at) && (
                        <div className="form-hint" style={{color:'var(--warn)'}}>Move-in date is before the date of joining at SSMCH. Confirm if intentional.</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Allotment Letter</label>
                      <button type="button" disabled className="form-input" style={{opacity:0.5,cursor:'not-allowed',textAlign:'left',fontSize:12}} title="Document uploads will be enabled in a later phase">+ Upload (deferred)</button>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">Financial Terms</div>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Monthly Rent / Token Amount <span className="req">*</span></label>
                      <div style={{position:'relative'}}>
                        <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>&#8377;</span>
                        <input type="number" min="0" className="form-input mono" style={{paddingLeft:26}} placeholder="0 if no rent / token amount" value={form.accom_rent} onChange={e => set('accom_rent', e.target.value)} data-testid="accom-rent"/>
                      </div>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Security Deposit</label>
                      <div style={{position:'relative'}}>
                        <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>&#8377;</span>
                        <input type="number" min="0" className="form-input mono" style={{paddingLeft:26}} placeholder="0 if none" value={form.accom_deposit} onChange={e => set('accom_deposit', e.target.value)} data-testid="accom-deposit"/>
                      </div>
                      <div className="form-hint">One-time refundable deposit, if applicable.</div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">Mess Service</div>
                  <div className="form-grid">
                    <div className="form-field span-2">
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                        <div>
                          <label className="form-label" style={{marginBottom:0}}>Mess Service Included</label>
                          <div className="form-hint" style={{marginTop:2}}>Default Yes for shared rooms; rare for independent flats.</div>
                        </div>
                        <label style={{position:'relative',display:'inline-block',width:38,height:20,cursor:'pointer',flexShrink:0}} data-testid="accom-mess-toggle">
                          <input type="checkbox" checked={form.accom_mess} onChange={e => set('accom_mess', e.target.checked)} style={{opacity:0,width:0,height:0}}/>
                          <span style={{position:'absolute',inset:0,borderRadius:20,background:form.accom_mess?'#16a34a':'#cbd5e1',transition:'background 0.2s'}}>
                            <span style={{position:'absolute',top:2,left:form.accom_mess?20:2,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 4px rgba(0,0,0,.25)'}}/>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div style={{overflow:'hidden',maxHeight:form.accom_mess?400:0,opacity:form.accom_mess?1:0,transition:'max-height 0.2s ease, opacity 0.2s ease'}}>
                    <div className="form-grid" style={{marginTop:12}}>
                      <div className="form-field">
                        <label className="form-label">Mess Plan <span className="req">*</span></label>
                        <select className="form-select" value={form.accom_mess_plan} onChange={e => set('accom_mess_plan', e.target.value)} data-testid="accom-mess-plan">
                          {MESS_PLANS.map(p => <option key={p}>{p}</option>)}
                        </select>
                        <div className="form-hint">Veg/Non-Veg preference. 'Special' covers Jain, diabetic, allergy-restricted, etc.</div>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Meals Covered <span className="req">*</span></label>
                        <div style={{display:'flex',gap:8,marginTop:4,flexWrap:'wrap'}}>
                          {MEALS.map(m => (
                            <label key={m} style={{display:'flex',alignItems:'center',gap:5,fontSize:13,padding:'5px 10px',borderRadius:6,border:'1px solid var(--border)',cursor:'pointer',background:form.accom_meals.includes(m)?'rgba(52,211,153,0.1)':'transparent'}} data-testid={`accom-meal-${m.toLowerCase()}`}>
                              <input type="checkbox" checked={form.accom_meals.includes(m)} onChange={e => {
                                if (e.target.checked) set('accom_meals', [...form.accom_meals, m]);
                                else set('accom_meals', form.accom_meals.filter(x => x !== m));
                              }}/>
                              {m}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Monthly Mess Deduction</label>
                        <div style={{position:'relative'}}>
                          <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>&#8377;</span>
                          <input type="number" min="0" className="form-input mono" style={{paddingLeft:26}} placeholder="0 if subsidised / no deduction" value={form.accom_mess_deduction} onChange={e => set('accom_mess_deduction', e.target.value)} data-testid="accom-mess-deduction"/>
                        </div>
                        <div className="form-hint">Auto-deducted from salary by HR, if applicable.</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">Household</div>
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Number of Family Members Residing <span className="req">*</span></label>
                      <input type="number" min="1" className="form-input mono" value={form.accom_family_count} onChange={e => set('accom_family_count', e.target.value)} data-testid="accom-family-count"/>
                      <div className="form-hint">Including the faculty member. Used for entitlement and security records.</div>
                      {Number(form.accom_family_count) > 20 && (
                        <div className="form-hint" style={{color:'var(--warn)'}}>Unusually high — please confirm.</div>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Pets</label>
                      <div style={{display:'flex',gap:0,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
                        {PETS_OPTIONS.map(opt => (
                          <button type="button" key={opt} onClick={() => set('accom_pets', opt)}
                            style={{flex:1,padding:'7px 4px',fontSize:11,fontWeight:form.accom_pets===opt?600:400,border:'none',cursor:'pointer',
                              background:form.accom_pets===opt?'var(--accent)':'var(--bg)',color:form.accom_pets===opt?'#fff':'var(--text-2)'}}
                            data-testid={`accom-pets-${opt.replace(/[\s()\/]/g,'').toLowerCase()}`}
                          >{opt}</button>
                        ))}
                      </div>
                      {form.accom_pets === "Yes (other)" && (
                        <input className="form-input" placeholder="Describe pet type" style={{marginTop:6}} value={form.accom_pets_detail} onChange={e => set('accom_pets_detail', e.target.value)} data-testid="accom-pets-detail"/>
                      )}
                    </div>
                    <div className="form-field span-2">
                      <label className="form-label">Notes / Special Conditions</label>
                      <textarea className="form-input" placeholder="e.g. spouse also a faculty member; sharing with Dr. X; specific dietary restrictions; accessibility requirements" style={{minHeight:64}} value={form.accom_notes} onChange={e => set('accom_notes', e.target.value)} data-testid="accom-notes"/>
                    </div>
                  </div>
                </div>

                <div style={{fontSize:11,color:'var(--text-3)',marginBottom:8}}>
                  If both spouses are SSMCH faculty sharing this allotment, record details under only ONE of them and reference the spouse in the Notes field.
                </div>

                <div style={{background:'rgba(107,156,255,0.06)',border:'1px solid rgba(107,156,255,0.2)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-2)'}}>
                  Recording the current allotment. Future re-allotments, mess changes, or vacating will be logged separately from the faculty's profile.
                </div>
              </div>
            </>
          )}

          {stepKey === "compliance" && (() => {
            const passed = compChecks.filter(c => c.status === "Pass").length;
            const warnings = compChecks.filter(c => c.status === "Warning");
            const fails = compChecks.filter(c => c.status === "Fail");
            const total = compChecks.length;
            const overall = fails.length > 0 ? "Non-Compliant" : warnings.length > 0 ? "Compliant with warnings" : "Compliant";
            const bannerBg = fails.length > 0 ? "rgba(248,113,113,0.08)" : warnings.length > 0 ? "rgba(251,191,36,0.08)" : "rgba(52,211,153,0.08)";
            const bannerBorder = fails.length > 0 ? "#f87171" : warnings.length > 0 ? "#fbbf24" : "#34d399";
            const bannerColor = fails.length > 0 ? "#dc2626" : warnings.length > 0 ? "#b45309" : "#059669";
            const categories = ["Age & Eligibility","Qualifications & Designation Compatibility","Registration","Experience","Continuing Education & Output"];
            return (
            <>
              <div style={{background:bannerBg,border:`1px solid ${bannerBorder}`,borderRadius:12,padding:'14px 18px',marginBottom:18,display:'flex',justifyContent:'space-between',alignItems:'center'}} data-testid="compliance-banner">
                <div>
                  <div style={{fontWeight:600,fontSize:15,color:bannerColor}}>{overall}</div>
                  <div style={{fontSize:12,color:'var(--text-2)',marginTop:2}}>
                    {passed} of {total} checks pass
                    {warnings.length > 0 && <span> · {warnings.length} warning{warnings.length>1?'s':''}</span>}
                    {fails.length > 0 && <span> · {fails.length} failure{fails.length>1?'s':''}</span>}
                  </div>
                </div>
                <button type="button" className="btn" style={{fontSize:11}} onClick={() => setCompChecks(runComplianceChecks(form))} data-testid="compliance-rerun">Re-run Checks</button>
              </div>

              {/* Manual NMC inputs (preserved from original) */}
              <div className="form-section">
                <div className="form-section-title">Manual NMC Inputs</div>
                <div className="form-grid">
                  <div className="form-field"><label className="form-label">Years post-PG</label><input type="number" step="0.5" className="form-input mono" value={form.post_pg_years} onChange={e => set('post_pg_years', e.target.value)}/></div>
                  <div className="form-field"><label className="form-label">Years in current role</label><input type="number" step="0.5" className="form-input mono" value={form.years_in_role} onChange={e => set('years_in_role', e.target.value)}/></div>
                  <div className="form-field"><label className="form-label">Indexed Publications</label><input type="number" className="form-input mono" value={form.publications} onChange={e => set('publications', e.target.value)}/></div>
                  <div className="form-field">
                    <label className="form-label">Courses</label>
                    <div style={{display:'flex', gap:14, marginTop:4}}>
                      <label style={{display:'flex',gap:6,alignItems:'center',fontSize:13}}><input type="checkbox" checked={form.met_done} onChange={e => set('met_done', e.target.checked)}/>MET completed</label>
                      <label style={{display:'flex',gap:6,alignItems:'center',fontSize:13}}><input type="checkbox" checked={form.bmr_done} onChange={e => set('bmr_done', e.target.checked)}/>BMR completed</label>
                    </div>
                  </div>
                </div>
              </div>

              {categories.map(cat => {
                const catChecks = compChecks.filter(c => c.category === cat);
                if (catChecks.length === 0) return null;
                return (
                  <div key={cat} className="form-section" style={{padding:14}}>
                    <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'var(--text-3)',fontWeight:600,marginBottom:10}}>{cat}</div>
                    {catChecks.map((ck, i) => {
                      const icon = ck.status === "Pass" ? "✓" : ck.status === "Warning" ? "!" : "✕";
                      const iconColor = ck.status === "Pass" ? "#059669" : ck.status === "Warning" ? "#b45309" : "#dc2626";
                      const rowBg = ck.status === "Pass" ? "rgba(52,211,153,0.04)" : ck.status === "Warning" ? "rgba(251,191,36,0.04)" : "rgba(248,113,113,0.06)";
                      return (
                        <div key={ck.check_id}>
                          <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 8px',background:rowBg,borderRadius:8,marginBottom:i<catChecks.length-1?6:0}} data-testid={`check-${ck.check_id}`}>
                            <span style={{fontSize:16,fontWeight:700,color:iconColor,lineHeight:1,marginTop:1,flexShrink:0,width:20,textAlign:'center'}}>{icon}</span>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontWeight:500,fontSize:13}}>{ck.name}</div>
                              <div className="mono" style={{fontSize:11,color:'var(--text-2)',marginTop:2}}>Expected: {ck.expected} · Actual: {ck.actual}</div>
                              <div style={{fontSize:10,color:'var(--text-3)',fontStyle:'italic',marginTop:2}}>{ck.nmc_reference}</div>
                            </div>
                            <div style={{flexShrink:0}}>
                              {ck.status === "Fail" && (
                                <button type="button" className="btn" style={{fontSize:10,padding:'4px 10px',color:'#dc2626'}} onClick={() => setStep(ck.fix_step)} data-testid={`fix-${ck.check_id}`}>Go fix →</button>
                              )}
                              {ck.status === "Warning" && !compAcks[ck.check_id]?.acknowledged && (
                                <button type="button" className="btn" style={{fontSize:10,padding:'4px 10px',color:'#b45309'}} onClick={() => setCompAcks(prev => ({...prev, [ck.check_id]: {...(prev[ck.check_id]||{}), open: true}}))} data-testid={`ack-${ck.check_id}`}>Acknowledge</button>
                              )}
                              {ck.status === "Warning" && compAcks[ck.check_id]?.acknowledged && (
                                <span style={{fontSize:11,color:'#059669',fontWeight:500}}>Acknowledged</span>
                              )}
                            </div>
                          </div>
                          {ck.status === "Warning" && compAcks[ck.check_id]?.open && !compAcks[ck.check_id]?.acknowledged && (
                            <div style={{background:'var(--bg-alt)',borderRadius:8,padding:'10px 12px',margin:'4px 0 8px 30px',border:'1px solid var(--border)'}}>
                              <label style={{display:'flex',gap:8,alignItems:'center',fontSize:12,cursor:'pointer'}}>
                                <input type="checkbox" checked={!!compAcks[ck.check_id]?.acknowledged} onChange={e => setCompAcks(prev => ({...prev, [ck.check_id]: {...prev[ck.check_id], acknowledged: e.target.checked}}))} data-testid={`ack-check-${ck.check_id}`}/>
                                I acknowledge this warning and accept responsibility for proceeding.
                              </label>
                              <textarea className="form-input" placeholder="Reason for proceeding (optional)" style={{marginTop:8,minHeight:40,fontSize:12}} value={compAcks[ck.check_id]?.note || ""} onChange={e => setCompAcks(prev => ({...prev, [ck.check_id]: {...prev[ck.check_id], note: e.target.value}}))} data-testid={`ack-note-${ck.check_id}`}/>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              <div style={{background:'rgba(107,156,255,0.06)',border:'1px solid rgba(107,156,255,0.2)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-2)'}}>
                Verifying against NMC Teachers Eligibility Qualifications Regulations 2022 (Gazette of India, 14 Feb 2022). {fails.length > 0 ? 'Resolve hard failures before save.' : warnings.length > 0 ? 'Acknowledge all warnings to proceed.' : 'All checks pass. Ready to save.'}
              </div>
            </>
          );})()}

          {stepKey === "review" && (() => {
            const corrAddr = [form.corr_house, form.corr_area, form.corr_post_office, form.corr_city, form.corr_state, form.corr_pin].filter(Boolean).join(", ");
            const permAddr = form.permanent_address_same ? corrAddr : [form.perm_house, form.perm_area, form.perm_post_office, form.perm_city, form.perm_state, form.perm_pin].filter(Boolean).join(", ");
            const docs = [
              form.aadhaar_card_name && "Aadhaar Card",
              form.pan_card_name && "PAN Card",
              form.photo_name && "Photo",
              form.appointment_letter_name && "Appointment Letter",
              form.bank_passbook_name && "Bank Passbook",
              form.ug_degree_name && "UG Degree",
              form.ug_reg_cert_name && "UG Reg. Cert",
              form.pg_degree_name && "PG Degree",
              form.pg_reg_cert_name && "PG Reg. Cert",
              form.diploma_cert_name && "Diploma Cert",
              form.dnb_cert_name && "DNB Cert",
              form.dnb_reg_cert_name && "DNB Reg. Cert",
              form.phd_degree_name && "PhD Degree",
              form.phd_reg_cert_name && "PhD Reg. Cert",
            ].filter(Boolean);
            const failCt = compChecks.filter(c => c.status === "Fail").length;
            const warnCt = compChecks.filter(c => c.status === "Warning").length;
            const passCt = compChecks.filter(c => c.status === "Pass").length;
            const compColor = failCt > 0 ? "#dc2626" : warnCt > 0 ? "#b45309" : "#059669";
            const compLabel = failCt > 0 ? "Non-Compliant" : warnCt > 0 ? "Compliant with warnings" : "Compliant";
            const R = ({label, val, mono}) => val ? <div className="kv-row"><div className="k">{label}</div><div className={`v ${mono?'mono':'sans'}`}>{val}</div></div> : null;
            const Sec = ({title, stepIdx, children}) => (
              <div className="form-section" style={{padding:'14px 16px',marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'var(--text-3)',fontWeight:600}}>{title}</div>
                  <button type="button" className="btn" style={{fontSize:10,padding:'3px 10px'}} onClick={() => setStep(stepIdx)}>Edit</button>
                </div>
                <div className="kv-grid">{children}</div>
              </div>
            );
            return (
            <div data-testid="wiz-review">
              {/* Step 1 — Identity */}
              <Sec title="Step 1 · Identity" stepIdx={0}>
                <R label="Full Name" val={`${form.title} ${form.full_name}`}/>
                <R label="Date of Birth" val={form.dob} mono/>
                <R label="Age" val={form.age ? `${form.age} years` : null} mono/>
                <R label="Blood Group" val={form.blood_group}/>
                <R label="Gender" val={form.gender}/>
                <R label="Marital Status" val={form.marital_status}/>
                <R label="Aadhaar" val={form.aadhaar ? `XXXX XXXX ${(form.aadhaar||"").replace(/\s/g,"").slice(-4)}` : null} mono/>
                <R label="PAN" val={form.pan} mono/>
                <R label="WhatsApp" val={form.whatsapp} mono/>
                <R label="Personal Email" val={form.personal_email}/>
                <R label="Correspondence Address" val={corrAddr}/>
                {!form.permanent_address_same && <R label="Permanent Address" val={permAddr}/>}
                {form.permanent_address_same && <R label="Permanent Address" val="Same as correspondence"/>}
              </Sec>

              {/* Step 2 — Emergency Contact */}
              <Sec title="Step 2 · Emergency Contact" stepIdx={1}>
                <R label="Contact Name" val={form.emergency_contact_name}/>
                <R label="Relation" val={form.emergency_contact_relation}/>
                <R label="Phone" val={form.emergency_contact_phone} mono/>
              </Sec>

              {/* Step 3 — Position at SSMCH */}
              <Sec title="Step 3 · Position at SSMCH" stepIdx={2}>
                <R label="Date of Joining" val={form.joined_at} mono/>
                <R label="Department" val={form.department}/>
                <R label="Designation" val={form.designation}/>
                <R label="Nature of Employment" val={form.employment_type}/>
                <R label="Appointment Letter No." val={form.appointment_letter_no} mono/>
              </Sec>

              {/* Step 4 — Bank, Salary & IDs */}
              <Sec title="Step 4 · Bank, Salary & IDs" stepIdx={3}>
                <R label="IFSC Code" val={form.ifsc_code} mono/>
                <R label="Bank" val={form.bank_name ? `${form.bank_name}${form.bank_branch ? `, ${form.bank_branch}` : ""}` : null}/>
                <R label="Account Number" val={form.account_number} mono/>
                <R label="Monthly Salary" val={form.monthly_salary ? `₹${Number(form.monthly_salary).toLocaleString('en-IN')}` : null} mono/>
                <R label="Salary Effective From" val={form.salary_effective_from} mono/>
                <R label="GreytHR ID" val={form.greyt_hr_id} mono/>
                <R label="AEBAS ID" val={form.aebas_id} mono/>
                <R label="Official Email" val={form.official_email}/>
                <R label="ID Card Status" val={form.id_card_status}/>
              </Sec>

              {/* Step 5 — Qualifications */}
              <Sec title="Step 5 · Qualifications" stepIdx={4}>
                <R label="UG Course" val={form.ug_course}/>
                <R label="UG College" val={form.ug_college}/>
                <R label="UG Period" val={form.ug_begin_year && form.ug_complete_year ? `${form.ug_begin_year} – ${form.ug_complete_year}` : null} mono/>
                <R label="UG Council" val={form.ug_council}/>
                <R label="UG Registration" val={form.ug_reg_date} mono/>
                {form.has_diploma && <>
                  <R label="Diploma" val={form.diploma_title}/>
                  <R label="Diploma Institute" val={form.diploma_institute}/>
                  <R label="Diploma Year" val={form.diploma_year} mono/>
                </>}
                <R label="PG Category" val={form.pg_category}/>
                <R label="PG Title" val={form.pg_title}/>
                <R label="PG College" val={form.pg_college}/>
                <R label="PG Period" val={form.pg_begin_year && form.pg_complete_year ? `${form.pg_begin_year} – ${form.pg_complete_year}` : null} mono/>
                <R label="PG Council" val={form.pg_council}/>
                <R label="PG Registration" val={form.pg_reg_date} mono/>
                {form.has_dnb && <>
                  <R label="DNB Institute" val={form.dnb_institute}/>
                  <R label="DNB Period" val={form.dnb_begin_year && form.dnb_complete_year ? `${form.dnb_begin_year} – ${form.dnb_complete_year}` : null} mono/>
                  <R label="DNB Council" val={form.dnb_council}/>
                </>}
                {form.has_phd && <>
                  <R label="PhD Title" val={form.phd_title}/>
                  <R label="PhD College" val={form.phd_college}/>
                  <R label="PhD Period" val={form.phd_begin_year && form.phd_complete_year ? `${form.phd_begin_year} – ${form.phd_complete_year}` : null} mono/>
                </>}
                {form.has_ss && <>
                  <R label="Super-Specialty" val={form.ss_title}/>
                  <R label="SS College" val={form.ss_college}/>
                  <R label="SS Period" val={form.ss_begin_year && form.ss_complete_year ? `${form.ss_begin_year} – ${form.ss_complete_year}` : null} mono/>
                  <R label="SS Council" val={form.ss_council}/>
                </>}
              </Sec>

              {/* Step 6 — Past Experience */}
              <Sec title="Step 6 · Past Experience" stepIdx={5}>
                {form.past_experience.length === 0 ? (
                  <div className="kv-row"><div className="k">Positions</div><div className="v sans" style={{color:'var(--text-3)'}}>None — first teaching post</div></div>
                ) : (
                  <div style={{width:'100%'}}>
                    {form.past_experience.map((p, i) => (
                      <div key={i} style={{padding:'8px 0',borderBottom:i < form.past_experience.length-1 ? '1px solid var(--border)' : 'none'}}>
                        <div style={{fontWeight:500,fontSize:13}}>{p.designation} — {p.department || 'N/A'}</div>
                        <div style={{fontSize:12,color:'var(--text-2)',marginTop:2}}>{p.institution} ({p.institution_type || '—'})</div>
                        <div className="mono" style={{fontSize:11,color:'var(--text-3)',marginTop:2}}>{p.from_date} to {p.to_date} · {p.nature_of_employment} · NMC: {p.nmc_recognised}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Sec>

              {/* Step 7 — Accommodation */}
              <Sec title="Step 7 · Accommodation" stepIdx={6}>
                {!form.accom_needed ? (
                  <div className="kv-row"><div className="k">Status</div><div className="v sans" style={{color:'var(--text-3)'}}>Self-arranged — no institutional accommodation</div></div>
                ) : (<>
                  <R label="Type" val={form.accom_type}/>
                  <R label="Block / Wing" val={form.accom_block}/>
                  <R label="Quarter / Flat" val={form.accom_quarter}/>
                  <R label="Furnished" val={form.accom_furnished}/>
                  <R label="Allotment Date" val={form.accom_allotment_date} mono/>
                  <R label="Move-in Date" val={form.accom_move_in} mono/>
                  <R label="Monthly Rent" val={`₹${Number(form.accom_rent || 0).toLocaleString('en-IN')}`} mono/>
                  {form.accom_deposit && <R label="Security Deposit" val={`₹${Number(form.accom_deposit).toLocaleString('en-IN')}`} mono/>}
                  <R label="Mess Service" val={form.accom_mess ? `Yes — ${form.accom_mess_plan} (${form.accom_meals.join(', ')})` : 'No'}/>
                  <R label="Family Members" val={form.accom_family_count} mono/>
                  {form.accom_pets !== "None" && <R label="Pets" val={form.accom_pets === "Yes (other)" ? `${form.accom_pets}: ${form.accom_pets_detail}` : form.accom_pets}/>}
                </>)}
              </Sec>

              {/* Step 8 — NMC Compliance */}
              <Sec title="Step 8 · NMC Compliance" stepIdx={7}>
                <div className="kv-row">
                  <div className="k">Status</div>
                  <div className="v sans" style={{color: compColor, fontWeight:600}}>{compLabel} ({passCt}/{compChecks.length} pass)</div>
                </div>
                <R label="Years post-PG" val={form.post_pg_years} mono/>
                <R label="Years in current role" val={form.years_in_role} mono/>
                <R label="Indexed Publications" val={String(form.publications)} mono/>
                <R label="MET" val={form.met_done ? 'Completed' : 'Not completed'}/>
                <R label="BMR" val={form.bmr_done ? 'Completed' : 'Not completed'}/>
              </Sec>

              {/* Documents summary */}
              {docs.length > 0 && (
                <div className="form-section" style={{padding:'14px 16px',marginBottom:12}}>
                  <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'var(--text-3)',fontWeight:600,marginBottom:10}}>Uploaded Documents ({docs.length})</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {docs.map(d => (
                      <span key={d} style={{fontSize:11,padding:'4px 10px',borderRadius:6,background:'rgba(52,211,153,0.08)',border:'1px solid rgba(52,211,153,0.2)',color:'#059669'}}>{d}</span>
                    ))}
                  </div>
                </div>
              )}

              {error && <div style={{color:'var(--bad)', fontSize:13, marginTop:14}}>{error}</div>}
            </div>
          );})()}

          <div className="wiz-foot">
            <div className="progress">Step {step+1} / {STEPS.length}</div>
            <div className="wiz-foot-btns">
              {step > 0 && <button className="btn" onClick={() => setStep(s => s-1)} data-testid="wiz-back">← Back</button>}
              {step < STEPS.length - 1 && (
                <button className="btn primary" onClick={() => setStep(s => s+1)} data-testid="wiz-continue">Continue →</button>
              )}
              {step === STEPS.length - 1 && (
                <button className="btn gold" disabled={saving} onClick={submit} data-testid="wiz-save">{saving ? 'Saving…' : isEdit ? 'Update Faculty' : 'Save Faculty'}</button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
